// Cookie utility functions
const CookieUtils = {
  // Set cookie with expiration
  setCookie: function(name, value, days = 30) {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
      const expiresString = expires.toUTCString();
      
      document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; expires=${expiresString}; path=/; SameSite=Lax`;
      return true;
    } catch (e) {
      console.warn('Cookie storage failed:', e);
      return false;
    }
  },
  
  // Get cookie value
  getCookie: function(name) {
    try {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) {
          const value = decodeURIComponent(c.substring(nameEQ.length, c.length));
          return JSON.parse(value);
        }
      }
      return null;
    } catch (e) {
      console.warn('Cookie retrieval failed:', e);
      return null;
    }
  },
  
  // Delete cookie
  deleteCookie: function(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
};

// Storage utility functions with safety checks
const StorageUtils = {
  // Check if storage is available
  isStorageAvailable: function(type) {
    try {
      const storage = window[type];
      const test = '__storage_test__';
      storage.setItem(test, test);
      storage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  },
  
  // Safely set item with fallbacks
  setItem: function(key, value) {
    // Try localStorage first
    if (this.isStorageAvailable('localStorage')) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
        return 'localStorage';
      } catch (e) {
        console.warn('localStorage setItem failed:', e);
      }
    }
    
    // Try sessionStorage
    if (this.isStorageAvailable('sessionStorage')) {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
        return 'sessionStorage';
      } catch (e) {
        console.warn('sessionStorage setItem failed:', e);
      }
    }
    
    // Try cookies
    if (CookieUtils.setCookie(key, value)) {
      return 'cookies';
    }
    
    // Fallback to memory
    if (!window._memoryStorage) {
      window._memoryStorage = {};
    }
    window._memoryStorage[key] = JSON.stringify(value);
    return 'memory';
  },
  
  // Safely get item with fallbacks
  getItem: function(key) {
    // Try localStorage first
    if (this.isStorageAvailable('localStorage')) {
      try {
        const data = localStorage.getItem(key);
        if (data) return data;
      } catch (e) {
        console.warn('localStorage getItem failed:', e);
      }
    }
    
    // Try sessionStorage
    if (this.isStorageAvailable('sessionStorage')) {
      try {
        const data = sessionStorage.getItem(key);
        if (data) return data;
      } catch (e) {
        console.warn('sessionStorage getItem failed:', e);
      }
    }
    
    // Try cookies
    const cookieData = CookieUtils.getCookie(key);
    if (cookieData) {
      return JSON.stringify(cookieData);
    }
    
    // Try memory storage
    if (window._memoryStorage && window._memoryStorage[key]) {
      return window._memoryStorage[key];
    }
    
    return null;
  }
};

// Updated size conversion data based on Google Sheet (E1:V33)
// Reference: https://docs.google.com/spreadsheets/d/1ikm3uXACzA475uo2x2kgE-QC3B0VGXj5XwkbO-SwQ6w/edit?usp=sharing
const sizeConversionData = {
  // Matrix-based bra size calculation (overbust vs underbust)
  // Format: [overbust][underbust] = bra_size (Column E)
  sizeMatrix: {
    24.5: { 24: "30A" },
    25: { 24: "30A" },
    25.5: { 24: "30B", 25: "30A" },
    26: { 24: "30B", 25: "30A" },
    26.5: { 24: "30B", 25: "30B", 26: "30A" },
    27: { 24: "30C", 25: "30B", 26: "30A" },
    27.5: { 24: "30C", 25: "30B", 26: "30B", 27: "30A" },
    28: { 24: "30C", 25: "30C", 26: "30B", 27: "30A" },
    28.5: { 24: "30D", 25: "30C", 26: "30B", 27: "30B", 28: "30A" },
    29: { 24: "30D", 25: "30C", 26: "30C", 27: "30B", 28: "30A" },
    29.5: { 24: "30D", 25: "30D", 26: "30C", 27: "30B", 28: "30B", 29: "32A" },
    30: { 24: "30D", 25: "30D", 26: "30C", 27: "30C", 28: "30B", 29: "32A" },
    30.5: { 24: "30E", 25: "30D", 26: "30D", 27: "30C", 28: "30B", 29: "32B", 30: "32A" },
    31: { 24: "30E", 25: "30D", 26: "30D", 27: "30C", 28: "30C", 29: "32B", 30: "32A" },
    31.5: { 24: "30F", 25: "30E", 26: "30D", 27: "30D", 28: "30C", 29: "32B", 30: "32B", 31: "34A" },
    32: { 24: "30F", 25: "30E", 26: "30D", 27: "30D", 28: "30C", 29: "32C", 30: "32B", 31: "34A" },
    32.5: { 25: "30F", 26: "30E", 27: "30D", 28: "30D", 29: "32C", 30: "32B", 31: "34B", 32: "34A" },
    33: { 25: "30F", 26: "30E", 27: "30D", 28: "30D", 29: "32C", 30: "32C", 31: "34B", 32: "34A" },
    33.5: { 26: "30F", 27: "30E", 28: "30D", 29: "32D", 30: "32C", 31: "34B", 32: "34B", 33: "36A" },
    34: { 26: "30F", 27: "30E", 28: "30D", 29: "32D", 30: "32C", 31: "34C", 32: "34B", 33: "36A" },
    34.5: { 27: "30F", 28: "30E", 29: "32D", 30: "32D", 31: "34C", 32: "34B", 33: "36B", 34: "36A" },
    35: { 27: "30F", 28: "30E", 29: "32D", 30: "32D", 31: "34C", 32: "34C", 33: "36B", 34: "36A" },
    35.5: { 28: "30F", 29: "32E", 30: "32D", 31: "34D", 32: "34C", 33: "36B", 34: "36B", 35: "36A" },
    36: { 28: "30F", 29: "32E", 30: "32D", 31: "34D", 32: "34C", 33: "36C", 34: "36B", 35: "36A" },
    36.5: { 29: "32F", 30: "32E", 31: "34D", 32: "34D", 33: "36C", 34: "36B", 35: "36B", 36: "36A" },
    37: { 29: "32F", 30: "32E", 31: "34D", 32: "34D", 33: "36C", 34: "36C", 35: "36B", 36: "36A" },
    37.5: { 30: "32F", 31: "34E", 32: "34D", 33: "36D", 34: "36C", 35: "36B", 36: "36B", 37: "38A" },
    38: { 30: "32F", 31: "34E", 32: "34D", 33: "36D", 34: "36C", 35: "36C", 36: "36B", 37: "38A" },
    38.5: { 31: "34F", 32: "34E", 33: "36D", 34: "36D", 35: "36C", 36: "36B", 37: "38B", 38: "38A" },
    39: { 31: "34F", 32: "34E", 33: "36D", 34: "36D", 35: "36C", 36: "36C", 37: "38B", 38: "38A" },
    39.5: { 32: "34F", 33: "36E", 34: "36D", 35: "36D", 36: "36C", 37: "38B", 38: "38B" },
    40: { 32: "34F", 33: "36E", 34: "36D", 35: "36D", 36: "36C", 37: "38C", 38: "38B" },
    40.5: { 33: "36F", 34: "36E", 35: "36D", 36: "36D", 37: "38C", 38: "38B" },
    41: { 33: "36F", 34: "36E", 35: "36D", 36: "36D", 37: "38C", 38: "38C" },
    41.5: { 34: "36F", 35: "36E", 36: "36D", 37: "38D", 38: "38C" },
    42: { 34: "36F", 35: "36E", 36: "36D", 37: "38D", 38: "38C" },
    42.5: { 35: "36F", 36: "36E", 37: "38D", 38: "38D" },
    43: { 35: "36F", 36: "36E", 37: "38D", 38: "38D" },
    43.5: { 36: "36F", 37: "38E", 38: "38D" },
    44: { 36: "36F", 37: "38E", 38: "38D" },
    44.5: { 37: "38E", 38: "38D" },
    45: { 37: "38E", 38: "38D" },
    45.5: { 37: "38F", 38: "38E" },
    46: { 37: "38F", 38: "38E" },
    46.5: { 38: "38F" },
    47: { 38: "38F" }
  },

  // Updated wireless size mapping from Google Sheet Column J (General Wireless Size)
  // This is used for quiz results and fallback for recommended size display
  getWirelessSize: function(braSize) {
    const wirelessMapping = {
      "30A": "XS", "30B": "XS", "30C": "S", "30D": "S", "30E": "M", "30F": "M",
      "32A": "XS", "32B": "S", "32C": "S", "32D": "M", "32E": "M", "32F": "L",
      "34A": "S", "34B": "S", "34C": "M", "34D": "M", "34E": "L", "34F": "L",
      "36A": "M", "36B": "M", "36C": "L", "36D": "L", "36E": "XL", "36F": "XL",
      "38A": "L", "38B": "L", "38C": "XL", "38D": "XL", "38E": "XL", "38F": "XL"
    };
    return wirelessMapping[braSize] || "M";
  },

  // Function to get brand-specific wireless size for recommended size display
  // Uses columns K-V from Google Sheet for brand-specific mappings
  getBrandWirelessSize: function(braSize, brandName) {
    if (!brandName || !braSize) {
      return this.getWirelessSize(braSize); // Fallback to general wireless size (Column J)
    }

    let brandKey = brandName.toLowerCase().trim();
    
    // Special handling for brands with multiple size formats
    brandKey = this.detectBrandFormat(brandKey);
    
    const brandMapping = this.brandSizes[brandKey];
    
    if (brandMapping && brandMapping[braSize]) {
      return brandMapping[braSize];
    }
    
    // Fallback to general wireless size (Column J) if no brand mapping found
    const fallbackSize = this.getWirelessSize(braSize);
    return fallbackSize;
  },

  // Sister sizes mapping (same as before)
  sisterSizes: {
    "30A": { down: "28B", up: "32AA" },
    "30B": { down: "28C", up: "32A" },
    "30C": { down: "28D", up: "32B" },
    "30D": { down: "28DD", up: "32C" },
    "30E": { down: "28F", up: "32DD" },
    "30F": { down: "28FF", up: "32E" },
    "32A": { down: "30B", up: "34AA" },
    "32B": { down: "30C", up: "34A" },
    "32C": { down: "30D", up: "34B" },
    "32D": { down: "30DD", up: "34C" },
    "32E": { down: "30F", up: "34DD" },
    "32F": { down: "30FF", up: "34E" },
    "34A": { down: "32B", up: "36AA" },
    "34B": { down: "32C", up: "36A" },
    "34C": { down: "32D", up: "36B" },
    "34D": { down: "32DD", up: "36C" },
    "34E": { down: "32F", up: "36DD" },
    "34F": { down: "32FF", up: "36E" },
    "36A": { down: "34B", up: "38AA" },
    "36B": { down: "34C", up: "38A" },
    "36C": { down: "34D", up: "38B" },
    "36D": { down: "34DD", up: "38C" },
    "36E": { down: "34F", up: "38DD" },
    "36F": { down: "34FF", up: "38E" },
    "38A": { down: "36B", up: "40AA" },
    "38B": { down: "36C", up: "40A" },
    "38C": { down: "36D", up: "40B" },
    "38D": { down: "36DD", up: "40C" },
    "38E": { down: "36F", up: "40DD" },
    "38F": { down: "36FF", up: "40E" }
  },

  // Brand-specific wireless sizing based on Google Sheet columns K-V
  // Used for recommended size display on product pages
  brandSizes: {
    "chantelle": {
      "30A": "XS/S", "30B": "XS/S", "30C": "XS/S", "30D": "XS/S", "30E": "XS/S", "30F": "XS/S",
      "32A": "XS/S", "32B": "XS/S", "32C": "XS/S", "32D": "M/L", "32E": "M/L", "32F": "M/L",
      "34A": "XS/S", "34B": "M/L", "34C": "M/L", "34D": "M/L", "34E": "M/L", "34F": "M/L",
      "36A": "M/L", "36B": "M/L", "36C": "M/L", "36D": "M/L", "36E": "M/L", "36F": "M/L",
      "38A": "M/L", "38B": "M/L", "38C": "M/L", "38D": "M/L", "38E": "M/L", "38F": "M/L"
    },
    "chantelle xs/s, m/l": {
      "30A": "XS/S", "30B": "XS/S", "30C": "XS/S", "30D": "XS/S", "30E": "XS/S", "30F": "XS/S",
      "32A": "XS/S", "32B": "XS/S", "32C": "XS/S", "32D": "M/L", "32E": "M/L", "32F": "M/L",
      "34A": "XS/S", "34B": "M/L", "34C": "M/L", "34D": "M/L", "34E": "M/L", "34F": "M/L",
      "36A": "M/L", "36B": "M/L", "36C": "M/L", "36D": "M/L", "36E": "M/L", "36F": "M/L"
    },
    "chantelle_single": {
      "30A": "XS", "30B": "XS", "30C": "XS", "30D": "S", "30E": "S", "30F": "S",
      "32A": "XS", "32B": "XS", "32C": "S", "32D": "S", "32E": "M", "32F": "M",
      "34A": "S", "34B": "S", "34C": "M", "34D": "M", "34E": "L", "34F": "L",
      "36A": "M", "36B": "M", "36C": "L", "36D": "L", "36E": "L", "36F": "L",
      "38A": "L", "38B": "L", "38C": "L", "38D": "L", "38E": "L", "38F": "L"
    },
    "agent provocateur": {
      "30A": "S", "30B": "S", "30C": "S", "30D": "S", "30E": "S", "30F": "S",
      "32A": "S", "32B": "S", "32C": "S", "32D": "M", "32E": "M", "32F": "M",
      "34A": "M", "34B": "M", "34C": "M", "34D": "M", "34E": "L", "34F": "L",
      "36A": "L", "36B": "L", "36C": "L", "36D": "L", "36E": "L", "36F": "L",
      "38A": "L", "38B": "L", "38C": "L", "38D": "XL", "38E": "XL", "38F": "XL"
    },
    "agent provocateur_numeric": {
      "30A": "2", "30B": "2", "30C": "2", "30D": "2", "30E": "2", "30F": "2",
      "32A": "2", "32B": "2", "32C": "2", "32D": "3", "32E": "3", "32F": "3",
      "34A": "3", "34B": "3", "34C": "3", "34D": "3", "34E": "4", "34F": "4",
      "36A": "4", "36B": "4", "36C": "4", "36D": "4", "36E": "4", "36F": "4",
      "38A": "4", "38B": "4", "38C": "4", "38D": "5", "38E": "5", "38F": "5"
    },
    "fleur du mal": {
      "30A": "XS", "30B": "XS", "30C": "XS", "30D": "XS", "30E": "XS", "30F": "XS",
      "32A": "XS", "32B": "XS", "32C": "S", "32D": "S", "32E": "S", "32F": "S",
      "34A": "S", "34B": "S", "34C": "M", "34D": "M", "34E": "M", "34F": "M",
      "36A": "M", "36B": "M", "36C": "L", "36D": "L", "36E": "L", "36F": "L"
    },
    "love stories": {
      "30A": "XS", "30B": "XS", "30C": "XS", "30D": "S", "30E": "S", "30F": "S",
      "32A": "XS", "32B": "XS", "32C": "S", "32D": "S", "32E": "M", "32F": "M",
      "34A": "S", "34B": "S", "34C": "M", "34D": "M", "34E": "L", "34F": "L",
      "36A": "M", "36B": "M", "36C": "L", "36D": "L", "36E": "", "36F": ""
    },
    "coco de mer": {
      "30A": "XS", "30B": "XS", "30C": "XS", "30D": "S", "30E": "S", "30F": "S",
      "32A": "XS", "32B": "XS", "32C": "S", "32D": "S", "32E": "M", "32F": "M",
      "34A": "S", "34B": "S", "34C": "M", "34D": "M", "34E": "L", "34F": "L",
      "36A": "M", "36B": "M", "36C": "L", "36D": "L", "36E": "", "36F": ""
    },
    "nette rose": {
      "30A": "XS", "30B": "XS", "30C": "XS", "30D": "S", "30E": "S", "30F": "S",
      "32A": "XS", "32B": "XS", "32C": "S", "32D": "S", "32E": "M", "32F": "M",
      "34A": "S", "34B": "M", "34C": "M", "34D": "M", "34E": "L", "34F": "L",
      "36A": "M", "36B": "M", "36C": "L", "36D": "L", "36E": "", "36F": ""
    },
    "bordelle": {
      "30A": "XS", "30B": "XS", "30C": "XS", "30D": "S", "30E": "S", "30F": "S",
      "32A": "XS", "32B": "XS", "32C": "S", "32D": "S", "32E": "M", "32F": "M",
      "34A": "S", "34B": "S", "34C": "M", "34D": "M", "34E": "L", "34F": "L",
      "36A": "M", "36B": "M", "36C": "L", "36D": "L", "36E": "", "36F": "",
      "38A": "L", "38B": "L", "38C": "L", "38D": "XL", "38E": "XL", "38F": "XL"
    },
    "bordelle_numeric": {
      "30A": "1", "30B": "1", "30C": "1", "30D": "2", "30E": "2", "30F": "2",
      "32A": "1", "32B": "1", "32C": "2", "32D": "2", "32E": "3", "32F": "3",
      "34A": "2", "34B": "2", "34C": "3", "34D": "3", "34E": "4", "34F": "4",
      "36A": "3", "36B": "3", "36C": "4", "36D": "4", "36E": "", "36F": "",
      "38A": "4", "38B": "4", "38C": "4", "38D": "5", "38E": "5", "38F": "5"
    },
    "kiki de montparnasse": {
      "30A": "XS", "30B": "XS", "30C": "XS", "30D": "S", "30E": "S", "30F": "S",
      "32A": "XS", "32B": "XS", "32C": "S", "32D": "M", "32E": "M", "32F": "M",
      "34A": "S", "34B": "M", "34C": "M", "34D": "M", "34E": "L", "34F": "L",
      "36A": "M", "36B": "M", "36C": "L", "36D": "L", "36E": "", "36F": ""
    },
    "fleur of england": {
      "30A": "XS", "30B": "XS", "30C": "S", "30D": "S", "30E": "M", "30F": "S",
      "32A": "XS", "32B": "XS", "32C": "S", "32D": "S", "32E": "L", "32F": "M",
      "34A": "XS", "34B": "S", "34C": "M", "34D": "M", "34E": "L", "34F": "L",
      "36A": "S", "36B": "M", "36C": "L", "36D": "L", "36E": "", "36F": ""
    }
  },

  // Function to detect brand format for brands with multiple size formats
  detectBrandFormat: function(brandKey) {
    switch (brandKey) {
      case 'chantelle':
        const chantelleFormat = detectChantelleFormat();
        
        if (chantelleFormat === 'single') {
          // Format 2: Single sizes (XS, S, M, L) - use chantelle_single mapping
          return 'chantelle_single';
        } else {
          // Format 1: Combined sizes (XS/S, S/M, M/L) - use chantelle mapping (Column K)
          return 'chantelle';
        }
        
      case 'bordelle':
        const bordelleFormat = detectBordelleFormat();
        
        if (bordelleFormat === 'numeric') {
          // Format 2: Numeric sizes (1, 2, 3, 4) - use bordelle_numeric mapping
          return 'bordelle_numeric';
        } else {
          // Format 1: Alphabet sizes (XS, S, M, L) - use bordelle mapping
          return 'bordelle';
        }
        
      case 'agent provocateur':
        const agentProvocateurFormat = detectAgentProvocateurFormat();
        
        if (agentProvocateurFormat === 'numeric') {
          // Format 2: Numeric sizes (1, 2, 3, 4, 5) - use agent provocateur_numeric mapping
          return 'agent provocateur_numeric';
        } else {
          // Format 1: Alphabet sizes (XS, S, M, L) - use agent provocateur mapping
          return 'agent provocateur';
        }
      
      // Add other brands with multiple formats here in the future
      // case 'other_brand':
      //   return detectOtherBrandFormat();
      
      default:
        // Return original brand key if no special format detection needed
        return brandKey;
    }
  },

  // Function to detect brand format for underwear brands with multiple size formats
  detectUnderwearBrandFormat: function(brandKey) {
    switch (brandKey) {
      case 'agent provocateur':
        const agentProvocateurUnderwearFormat = detectAgentProvocateurUnderwearFormat();
        
        if (agentProvocateurUnderwearFormat === 'numeric') {
          // Format 2: Numeric sizes (1, 2, 3, 4, 5) - use agent provocateur_numeric mapping
          return 'agent provocateur_numeric';
        } else {
          // Format 1: Alphabet sizes (XS, S, M, L) - use agent provocateur mapping
          return 'agent provocateur';
        }
        
      case 'bordelle':
        const bordelleUnderwearFormat = detectBordelleUnderwearFormat();
        
        if (bordelleUnderwearFormat === 'numeric') {
          // Format 2: Numeric sizes (1, 2, 3, 4, 5) - use bordelle_numeric mapping
          return 'bordelle_numeric';
        } else {
          // Format 1: Alphabet sizes (XS, S, M, L) - use bordelle mapping
          return 'bordelle';
        }
      
      // Add other underwear brands with multiple formats here in the future
      // case 'other_underwear_brand':
      //   return detectOtherUnderwearBrandFormat();
      
      default:
        // Return original brand key if no special format detection needed
        return brandKey;
    }
  },

  // Underwear size matrix (waist vs hip measurements)
  // Based on the reference chart from Google Sheets - Panty Size sheet
  // Reference: https://docs.google.com/spreadsheets/d/1ikm3uXACzA475uo2x2kgE-QC3B0VGXj5XwkbO-SwQ6w/edit?usp=sharing
  underwearSizeMatrix: {
    // Format: [waist][hip] = underwear_size
    // Updated to match spreadsheet exactly: waist 26 + hip 38 = M
    24: { 34: "XS", 35: "XS", 36: "S", 37: "S", 38: "M", 39: "M", 40: "L", 41: "L" },
    25: { 34: "XS", 35: "XS", 36: "S", 37: "S", 38: "M", 39: "M", 40: "L", 41: "L" },
    26: { 34: "S", 35: "S", 36: "S", 37: "S", 38: "M", 39: "M", 40: "L", 41: "L" },
    27: { 34: "S", 35: "S", 36: "S", 37: "S", 38: "M", 39: "M", 40: "L", 41: "L" },
    28: { 34: "M", 35: "M", 36: "M", 37: "M", 38: "M", 39: "M", 40: "L", 41: "L" },
    29: { 34: "M", 35: "M", 36: "M", 37: "M", 38: "M", 39: "M", 40: "L", 41: "L" },
    30: { 34: "L", 35: "L", 36: "L", 37: "L", 38: "L", 39: "L", 40: "L", 41: "L" },
    31: { 34: "L", 35: "L", 36: "L", 37: "L", 38: "L", 39: "L", 40: "L", 41: "L" },
    32: { 34: "L", 35: "L", 36: "L", 37: "L", 38: "L", 39: "L", 40: "L", 41: "L" }
  },

  // Underwear brand-specific sizing based on Google Sheet
  underwearBrandSizes: {
    "chantelle": {
      "XS": "XS", "S": "S", "M": "M", "L": "L"
    },
    "agent provocateur": {
      "XS": "1", "S": "2", "M": "3", "L": "4", "XL": "5"  
    },
    "agent provocateur_numeric": {
      "XS": "1", "S": "2", "M": "3", "L": "4", "XL": "5"  
    },
    "fleur du mal": {
      "XS": "XS", "S": "S", "M": "M", "L": "L", "XL": "L"
    },
    "love stories": {
      "XS": "XS", "S": "S", "M": "M", "L": "L", "XL": "L"
    },
    "coco de mer": {
      "XS": "XS", "S": "S", "M": "M", "L": "L", "XL": "L"
    },
    "nette rose": {
      "XS": "XS", "S": "S", "M": "M", "L": "L", "XL": "L"
    },
    "bordelle": {
      "XS": "XS", "S": "S", "M": "M", "L": "L", "XL": "L"
    },
    "bordelle_numeric": {
      "XS": "1", "S": "2", "M": "3", "L": "4", "XL": "5"
    },
    "kiki de montparnasse": {
      "XS": "XS", "S": "S", "M": "M", "L": "L", "XL": "L"
    },
    "fleur of england": {
      "XS": "XS", "S": "S", "M": "M", "L": "L", "XL": "L"
    }
  }
};

// Function to merge brand size mappings from Metaobjects with hardcoded values
// Metaobject values take precedence over hardcoded values
function mergeBrandSizeMappingsFromMetaobjects() {
  // Wait for metaobject data to be available (it loads asynchronously)
  if (typeof window.brandSizeMappingsFromMetaobjects === 'undefined') {
    // Data not loaded yet, try again after a short delay
    setTimeout(mergeBrandSizeMappingsFromMetaobjects, 100);
    return;
  }
  
  if (!window.brandSizeMappingsFromMetaobjects || !window.brandSizeMappingsFromMetaobjects.loaded) {
    return; // Use hardcoded values as fallback
  }
  
  const metaobjectMappings = window.brandSizeMappingsFromMetaobjects;
  
  // Merge bra mappings (metaobjects override hardcoded)
  if (metaobjectMappings.bra && Object.keys(metaobjectMappings.bra).length > 0) {
    sizeConversionData.brandSizes = {
      ...sizeConversionData.brandSizes, // Start with hardcoded values
      ...metaobjectMappings.bra // Override with metaobject values
    };
  }
  
  // Merge underwear mappings (metaobjects override hardcoded)
  if (metaobjectMappings.underwear && Object.keys(metaobjectMappings.underwear).length > 0) {
    sizeConversionData.underwearBrandSizes = {
      ...sizeConversionData.underwearBrandSizes, // Start with hardcoded values
      ...metaobjectMappings.underwear // Override with metaobject values
    };
  }
  
  // Update the recommended size display on product pages after mappings are loaded
  // This ensures the display shows the correct brand-specific size from metaobjects
  if (typeof updateRecommendedSizeDisplay === 'function') {
    updateRecommendedSizeDisplay();
  }
  
  // Update the wireless size field in the fit finder on product pages
  // Use a small delay to ensure the fit finder is fully rendered
  if (typeof updateWirelessSizeFieldOnProductPage === 'function') {
    setTimeout(() => {
      updateWirelessSizeFieldOnProductPage();
    }, 200);
  }
  
  // Also re-run variant auto-selection with the updated mappings
  if (typeof autoSelectRecommendedVariant === 'function') {
    autoSelectRecommendedVariant();
  }
}

// Function to update wireless size field on product pages after brand mappings load
function updateWirelessSizeFieldOnProductPage(retryCount = 0) {
  // Only run on product pages
  if (!window.location.pathname.includes('/products/')) {
    return;
  }
  
  // Check if fit finder is present and has results
  const wirelessSizeInput = document.getElementById('wirelessSize');
  if (!wirelessSizeInput) {
    // Element might not be rendered yet, try again after a short delay (max 10 retries = 1 second)
    if (retryCount < 10) {
      setTimeout(() => updateWirelessSizeFieldOnProductPage(retryCount + 1), 100);
    } else {
    }
    return;
  }
  
  // Get stored fit finder results
  const storedResults = getStoredSizeFinderResults();
  if (!storedResults) {
    return; // No stored results to update
  }
  
  try {
    const results = JSON.parse(storedResults);
    const baseBraSize = results.baseBraSize;

    if (!baseBraSize) {
      return;
    }
    
    // Get current product brand
    const currentBrand = getCurrentProductBrand();
    
    // Get brand-specific wireless size using the updated metaobject mappings
    const brandWirelessSize = sizeConversionData.getBrandWirelessSize(baseBraSize, currentBrand);
    
    // Update the wireless size input field
    const oldValue = wirelessSizeInput.value;
    wirelessSizeInput.value = brandWirelessSize;
    
    // Also update the stored results with the new brand-specific wireless size
    results.wirelessSize = brandWirelessSize;
    results.brandSize = brandWirelessSize;
    StorageUtils.setItem('sizeFinderResults', results);
    
  } catch (error) {
    console.error('❌ Error updating wireless size field:', error);
  }
}

// Initialize merging when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mergeBrandSizeMappingsFromMetaobjects);
} else {
  // DOM already loaded, but wait a bit for metaobject data to fetch
  setTimeout(mergeBrandSizeMappingsFromMetaobjects, 500);
}

// Updated function to get current product brand
function getCurrentProductBrand() {
  let brandName = null;
  
  
  // Method 1: Try to get from window.currentProduct (set by Liquid)
  if (window.currentProduct && window.currentProduct.vendor) {
    brandName = window.currentProduct.vendor;
  }
  
  // Method 2: Try to get from global product object (if available)
  else if (typeof product !== 'undefined' && product && product.vendor) {
    brandName = product.vendor;
  }
  
  // Method 3: Try to get from window.product
  else if (window.product && window.product.vendor) {
    brandName = window.product.vendor;
  }
  
  // Method 4: Try to get from product data attributes
  else {
    const brandElement = document.querySelector('[data-product-vendor]');
    if (brandElement) {
      brandName = brandElement.textContent || brandElement.getAttribute('data-product-vendor');
    }
  }
  
  // Method 5: Try to get from product JSON script tag
  if (!brandName) {
    const productScript = document.querySelector('script[type="application/json"][data-product-json]');
    if (productScript) {
      try {
        const productData = JSON.parse(productScript.textContent);
        if (productData && productData.vendor) {
          brandName = productData.vendor;
        }
      } catch (e) {
      }
    }
  }
  
  // Method 6: Try to extract from product title (fallback)
  if (!brandName) {
    const titleElement = document.querySelector('.product-title, .product__title, h1, [class*="product"][class*="title"]');
    if (titleElement) {
      const title = titleElement.textContent.toLowerCase();
      for (const brand in sizeConversionData.brandSizes) {
        if (title.includes(brand)) {
          brandName = brand;
          break;
        }
      }
    }
  }
  
  // Clean up brand name before returning
  if (brandName) {
    // Remove collaboration markers (e.g., " x Sheer", " X Partner")
    const cleanedBrandName = brandName.replace(/\s(x|X)\s.+/g, '').trim();
    if (cleanedBrandName !== brandName) {
      brandName = cleanedBrandName;
    }
  }
  
  // Return the brand name as lowercase and trimmed for consistent matching
  const finalBrand = brandName ? brandName.toLowerCase().trim() : null;
  
  return finalBrand;
}

// Modal functions
function openSizeFinder() {
  document.getElementById('sizeFinder').style.display = 'block';
  document.body.style.overflow = 'hidden';
  const header = document.querySelector('html.sticky-header-initialized .site-header');
  if (header) {
    header.style.zIndex = '0';
  }
  
  // Initialize tabs based on product type when modal opens
  initializeTabs();
  
  // Restore previous fit finder data if available
  restoreFitFinderData();
}

function closeSizeFinder() {
  document.getElementById('sizeFinder').style.display = 'none';
  document.body.style.overflow = 'auto';
  const header = document.querySelector('html.sticky-header-initialized .site-header');
    if (header) {
      header.style.zIndex = '';
    }
}

// Tab switching
function switchTab(tab) {
  // Remove active class from all tabs and sections
  document.querySelectorAll('.tab-btn').forEach(btn => btn.classList.remove('active'));
  document.querySelectorAll('.converter-section').forEach(section => section.classList.remove('active'));
  
  // Find the button element (in case user clicked on span inside button)
  let buttonElement = event.target;
  if (!buttonElement.classList.contains('tab-btn')) {
    buttonElement = buttonElement.closest('.tab-btn');
  }
  
  // Add active class to selected tab and section
  if (buttonElement) {
    buttonElement.classList.add('active');
  }
  document.getElementById(tab + 'Converter').classList.add('active');
}

// Function to initialize tabs based on product type
function initializeTabs() {
  const productType = getCurrentProductType();
  
  // Determine which tab should be active based on product type
  let defaultTab = 'bra'; // Default to bra
  
  if (productType) {
    const normalizedType = productType.toLowerCase();
    if (normalizedType === 'underwear') {
      defaultTab = 'underwear';
    } else if (normalizedType === 'bras') {
      defaultTab = 'bra';
    }
    // For other types (loungewear, etc.), default to bra
  }
  
  // Remove active class from all tabs and sections first
  document.querySelectorAll('.tab-btn').forEach(btn => btn.classList.remove('active'));
  document.querySelectorAll('.converter-section').forEach(section => section.classList.remove('active'));
  
  // Find and activate the appropriate tab button
  const tabButtons = document.querySelectorAll('.tab-btn');
  tabButtons.forEach(button => {
    const onclick = button.getAttribute('onclick');
    if (onclick && onclick.includes(`'${defaultTab}'`)) {
      button.classList.add('active');
    }
  });
  
  // Activate the corresponding converter section
  const converterSection = document.getElementById(defaultTab + 'Converter');
  if (converterSection) {
    converterSection.classList.add('active');
  }
}

// Updated calculation function using matrix lookup
function calculateBraSize() {
  const bustInput = document.getElementById('bustInput');
  const underBustInput = document.getElementById('underBustInput');
  const bustValue = parseFloat(bustInput.value);
  const underBustValue = parseFloat(underBustInput.value);
  
  if (!bustValue || !underBustValue) {
    alert('Please enter both bust and underbust measurements');
    return;
  }
  
  // Convert to inches if currently in cm
  const bustUnit = bustInput.getAttribute('data-unit') || 'in';
  const underBustUnit = underBustInput.getAttribute('data-unit') || 'in';
  const bust = bustUnit === 'cm' ? cmToInches(bustValue) : bustValue;
  const underBust = underBustUnit === 'cm' ? cmToInches(underBustValue) : underBustValue;
  
  if (bust <= underBust) {
    alert('Bust measurement should be larger than underbust measurement');
    return;
  }
  
  // Round measurements to nearest 0.5 for matrix lookup
  const roundedBust = Math.round(bust * 2) / 2;
  const roundedUnderBust = Math.round(underBust);
  
  // Look up size in matrix
  let matchedSize = null;
  
  // First try exact match
  if (sizeConversionData.sizeMatrix[roundedBust] && 
      sizeConversionData.sizeMatrix[roundedBust][roundedUnderBust]) {
    matchedSize = sizeConversionData.sizeMatrix[roundedBust][roundedUnderBust];
  }
  
  // If no exact match, try nearby measurements
  if (!matchedSize) {
    // Try +/- 0.5 inches for bust measurement
    const bustVariations = [roundedBust - 0.5, roundedBust + 0.5];
    const underBustVariations = [roundedUnderBust - 1, roundedUnderBust + 1];
    
    for (let bustVar of bustVariations) {
      for (let underBustVar of underBustVariations) {
        if (sizeConversionData.sizeMatrix[bustVar] && 
            sizeConversionData.sizeMatrix[bustVar][underBustVar]) {
          matchedSize = sizeConversionData.sizeMatrix[bustVar][underBustVar];
          break;
        }
      }
      if (matchedSize) break;
    }
  }
  
  if (!matchedSize) {
    alert('No matching size found for these measurements. Please check your measurements or try values within our size range.');
    return;
  }
  
  // Create result object with the matched size
  const result = {
    bra: matchedSize,
    wireless: sizeConversionData.getWirelessSize(matchedSize)
  };
  
  // Display results with measurements
  displayResults(result, bust, underBust);
}

function displayResults(matchedSize, bustMeasurement, underbustMeasurement) {
  const resultsSection = document.getElementById('braResults');
  const recommendedSize = document.getElementById('recommendedSize');
  const sisterDown = document.getElementById('sisterDown');
  const sisterUp = document.getElementById('sisterUp');
  const wirelessSize = document.getElementById('wirelessSize');
  
  // Set recommended size (Column E - Bra Size)
  recommendedSize.value = matchedSize.bra;
  
  // Set sister sizes
  const sisters = sizeConversionData.sisterSizes[matchedSize.bra];
  if (sisters) {
    sisterDown.value = sisters.down;
    sisterUp.value = sisters.up;
  } else {
    sisterDown.value = "N/A";
    sisterUp.value = "N/A";
  }
  
  // Set wireless size - use the value from matchedSize if available (brand-specific),
  // otherwise calculate using general mapping
  let wirelessSizeValue;
  if (matchedSize.wireless) {
    // Use the wireless size passed in (could be brand-specific from restoreFitFinderData)
    wirelessSizeValue = matchedSize.wireless;
  } else {
    // Calculate using general mapping
    wirelessSizeValue = sizeConversionData.getWirelessSize(matchedSize.bra);
  }
  
  wirelessSize.value = wirelessSizeValue;
  
  // Store the size finder results for cross-site use
  const sizeFinderResults = {
    bust: bustMeasurement,
    underbust: underbustMeasurement,
    baseBraSize: matchedSize.bra, // Column E - Bra Size
    timestamp: new Date().toISOString(),
    wirelessSize: wirelessSizeValue, // Use the wireless size (general or brand-specific)
    brandSize: null // Will be determined on product pages using getBrandWirelessSize
  };
  
  // Store using safe storage utility
  const storageType = StorageUtils.setItem('sizeFinderResults', sizeFinderResults);
  
  // Update any existing recommended size displays
  updateRecommendedSizeDisplay();
  
  // Show results
  resultsSection.style.display = 'block';
  resultsSection.scrollIntoView({ behavior: 'smooth' });
}

// Underwear size calculation function
function calculateUnderwearSize() {
  const waistInput = document.getElementById('waistInput');
  const hipInput = document.getElementById('hipInput');
  const waistValue = parseFloat(waistInput.value);
  const hipValue = parseFloat(hipInput.value);
  
  if (!waistValue || !hipValue) {
    alert('Please enter both waist and hip measurements');
    return;
  }
  
  // Convert to inches if currently in cm
  const waistUnit = waistInput.getAttribute('data-unit') || 'in';
  const hipUnit = hipInput.getAttribute('data-unit') || 'in';
  const waist = waistUnit === 'cm' ? cmToInches(waistValue) : waistValue;
  const hip = hipUnit === 'cm' ? cmToInches(hipValue) : hipValue;
  
  if (waist >= hip) {
    alert('Hip measurement should be larger than waist measurement');
    return;
  }
  
  // Round measurements to nearest whole number for matrix lookup
  const roundedWaist = Math.round(waist);
  const roundedHip = Math.round(hip);
  
  // Look up size in underwear matrix
  let matchedSize = null;
  
  // First try exact match
  if (sizeConversionData.underwearSizeMatrix[roundedWaist] && 
      sizeConversionData.underwearSizeMatrix[roundedWaist][roundedHip]) {
    matchedSize = sizeConversionData.underwearSizeMatrix[roundedWaist][roundedHip];
  }
  
  // If no exact match, try nearby measurements
  if (!matchedSize) {
    // Try +/- 1 inch for both measurements
    const waistVariations = [roundedWaist - 1, roundedWaist + 1];
    const hipVariations = [roundedHip - 1, roundedHip + 1];
    
    for (let waistVar of waistVariations) {
      for (let hipVar of hipVariations) {
        if (sizeConversionData.underwearSizeMatrix[waistVar] && 
            sizeConversionData.underwearSizeMatrix[waistVar][hipVar]) {
          matchedSize = sizeConversionData.underwearSizeMatrix[waistVar][hipVar];
          break;
        }
      }
      if (matchedSize) break;
    }
  }
  
  if (!matchedSize) {
    alert('No matching size found for these measurements. Please check your measurements or try values within our size range.');
    return;
  }
  
  // Display underwear results
  displayUnderwearResults(matchedSize, waist, hip);
}

// Function to display underwear results
function displayUnderwearResults(matchedSize, waistMeasurement, hipMeasurement) {
  const resultsSection = document.getElementById('underwearResults');
  const recommendedSize = document.getElementById('recommendedUnderwearSize');
  
  // Get current product brand
  const currentBrand = getCurrentProductBrand();
  
  // Check for brand-specific sizing for recommended-size-display
  let brandSize = null;
  if (currentBrand) {
    const brandKey = currentBrand.toLowerCase();
    
    // Use the underwear brand format detection to determine which mapping to use
    const detectedBrandKey = sizeConversionData.detectUnderwearBrandFormat(brandKey);

    const brandMapping = sizeConversionData.underwearBrandSizes[detectedBrandKey];

    if (brandMapping && brandMapping[matchedSize]) {
      brandSize = brandMapping[matchedSize];
    }
  }
  
  // Always use the alphabet format size for recommendedUnderwearSize input field
  // This ensures consistent format in the size finder modal
  const finalSize = matchedSize; // Always use the base alphabet size (XS, S, M, L, XL)
  recommendedSize.value = finalSize;
  
  // Store the underwear size finder results for cross-site use
  const underwearSizeFinderResults = {
    waist: waistMeasurement,
    hip: hipMeasurement,
    baseUnderwearSize: matchedSize, // Base size from matrix (always alphabet format)
    timestamp: new Date().toISOString(),
    brandSize: brandSize || null // Brand-specific size for recommended-size-display
  };
  
  // Store using safe storage utility
  const storageType = StorageUtils.setItem('underwearSizeFinderResults', underwearSizeFinderResults);
  
  // Update any existing recommended size displays
  updateRecommendedSizeDisplay();
  
  // Show results
  resultsSection.style.display = 'block';
  resultsSection.scrollIntoView({ behavior: 'smooth' });
}

// International size conversion data based on Google Sheet
const internationalSizeData = {
  // Primary size (US) as base reference
  '30A': { US: '30A', UK: '30A', EU: '65A', FR: '80A', JP: '65A', AU: '8A' },
  '30B': { US: '30B', UK: '30B', EU: '65B', FR: '80B', JP: '65B', AU: '8B' },
  '30C': { US: '30C', UK: '30C', EU: '65C', FR: '80C', JP: '65C', AU: '8C' },
  '30D': { US: '30D', UK: '30D', EU: '65D', FR: '80D', JP: '65D', AU: '8D' },
  '30E': { US: '30E', UK: '30DD', EU: '65E', FR: '80E', JP: '65E', AU: '8DD' },
  '30F': { US: '30F', UK: '30E', EU: '65F', FR: '80F', JP: '65F', AU: '8E' },
  '32A': { US: '32A', UK: '32A', EU: '70A', FR: '85A', JP: '70A', AU: '10A' },
  '32B': { US: '32B', UK: '32B', EU: '70B', FR: '85B', JP: '70B', AU: '10B' },
  '32C': { US: '32C', UK: '32C', EU: '70C', FR: '85C', JP: '70C', AU: '10C' },
  '32D': { US: '32D', UK: '32D', EU: '70D', FR: '85D', JP: '70D', AU: '10D' },
  '32E': { US: '32E', UK: '32DD', EU: '70E', FR: '85E', JP: '70E', AU: '10DD' },
  '32F': { US: '32F', UK: '32E', EU: '70F', FR: '85F', JP: '70F', AU: '10E' },
  '34A': { US: '34A', UK: '34A', EU: '75A', FR: '90A', JP: '75A', AU: '12A' },
  '34B': { US: '34B', UK: '34B', EU: '75B', FR: '90B', JP: '75B', AU: '12B' },
  '34C': { US: '34C', UK: '34C', EU: '75C', FR: '90C', JP: '75C', AU: '12C' },
  '34D': { US: '34D', UK: '34D', EU: '75D', FR: '90D', JP: '75D', AU: '12D' },
  '34E': { US: '34E', UK: '34DD', EU: '75E', FR: '90E', JP: '75E', AU: '12DD' },
  '34F': { US: '34F', UK: '34E', EU: '75F', FR: '90F', JP: '75F', AU: '12E' },
  '36A': { US: '36A', UK: '36A', EU: '80A', FR: '95A', JP: '80A', AU: '14A' },
  '36B': { US: '36B', UK: '36B', EU: '80B', FR: '95B', JP: '80B', AU: '14B' },
  '36C': { US: '36C', UK: '36C', EU: '80C', FR: '95C', JP: '80C', AU: '14C' },
  '36D': { US: '36D', UK: '36D', EU: '80D', FR: '95D', JP: '80D', AU: '14D' },
  '36E': { US: '36E', UK: '36DD', EU: '80E', FR: '95E', JP: '80E', AU: '14DD' },
  '36F': { US: '36F', UK: '36E', EU: '80F', FR: '95F', JP: '80F', AU: '14E' },
  '38A': { US: '38A', UK: '38A', EU: '85A', FR: '100A', JP: '85A', AU: '16A' },
  '38B': { US: '38B', UK: '38B', EU: '85B', FR: '100B', JP: '85B', AU: '16B' },
  '38C': { US: '38C', UK: '38C', EU: '85C', FR: '100C', JP: '85C', AU: '16C' },
  '38D': { US: '38D', UK: '38D', EU: '85D', FR: '100D', JP: '85D', AU: '16D' },
  '38E': { US: '38E', UK: '38DD', EU: '85E', FR: '100E', JP: '85E', AU: '16DD' },
  '38F': { US: '38F', UK: '38E', EU: '85F', FR: '100F', JP: '85F', AU: '16E' }
};

// International underwear size conversion data based on Google Sheet "Panty Size" sheet
// Updated with correct size ranges: AU/UK: 4–6,8–10,10–12,14–16; US: 0–2,4–6,6–8,10–12
const underwearInternationalSizeData = {
  // Primary size (US) as base reference for underwear with proper international conversions
  'XS': { 
    US: ['XS', '0-2', '0', '2'], 
    UK: ['4-6', '4', '6'], 
    EU: ['32', '34'], 
    FR: ['34', '36'], 
    JP: ['XS'], 
    AU: ['4-6', '4', '6'] 
  },
  'S': { 
    US: ['S', '4-6', '4', '6'], 
    UK: ['8-10', '8', '10'], 
    EU: ['34', '36'], 
    FR: ['36', '38'], 
    JP: ['S'], 
    AU: ['8-10', '8', '10'] 
  },
  'M': { 
    US: ['M', '6-8', '6', '8'], 
    UK: ['10-12', '10', '12'], 
    EU: ['36', '38'], 
    FR: ['38', '40'], 
    JP: ['M'], 
    AU: ['10-12', '10', '12'] 
  },
  'L': { 
    US: ['L', '10-12', '10', '12'], 
    UK: ['14-16', '14', '16'], 
    EU: ['38', '40'], 
    FR: ['40', '42'], 
    JP: ['L'], 
    AU: ['14-16', '14', '16'] 
  },
  'XL': { 
    US: ['XL', '14-16', '14', '16'], 
    UK: ['18-20', '18', '20'], 
    EU: ['40', '42'], 
    FR: ['42', '44'], 
    JP: ['XL'], 
    AU: ['18-20', '18', '20'] 
  }
};

// Function to find US underwear size from any international size
function findUSUnderwearSize(inputSize, inputCountry) {
  const normalizedInput = inputSize.toString().toUpperCase().trim();
  
  // If input is already US, check if it matches any US size format
  if (inputCountry === 'US') {
    // For US sizes, find which base size this input matches
    for (const [usSize, conversions] of Object.entries(underwearInternationalSizeData)) {
      if (conversions.US && conversions.US.includes(normalizedInput)) {
        return usSize;
      }
    }
    // If no specific match found, return as is for letter sizes
    if (['XS', 'S', 'M', 'L', 'XL', 'XXL'].includes(normalizedInput)) {
      return normalizedInput;
    }
  }
  
  // Search through all sizes to find the matching size for the input country
  for (const [usSize, conversions] of Object.entries(underwearInternationalSizeData)) {
    const countryValues = conversions[inputCountry];
    if (countryValues && Array.isArray(countryValues)) {
      // Check if input matches any of the size variations for this country
      for (const sizeVariation of countryValues) {
        if (sizeVariation.toString().toUpperCase() === normalizedInput) {
          return usSize;
        }
      }
    }
  }
  return null;
}

// Function to find US size from any international size
function findUSSize(inputSize, inputCountry) {
  const normalizedInput = inputSize.toUpperCase().trim();
  
  // If input is already US, return as is
  if (inputCountry === 'US') {
    return normalizedInput;
  }
  
  // Search through all sizes to find the matching size for the input country
  for (const [usSize, conversions] of Object.entries(internationalSizeData)) {
    if (conversions[inputCountry] === normalizedInput) {
      return usSize;
    }
  }
  
  return null; // Size not found
}

// Function to detect if Chantelle product uses combined (XS/S) or single (XS) format
function detectChantelleFormat() {
  // Function to extract size values from various elements
  function extractSizeValues(selectors) {
    let sizeValues = [];
    
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        const text = element.textContent || element.value || element.getAttribute('data-value') || '';
        // Look for size patterns like XS, S, M, L, XL, XS/S, S/M, M/L
        // Also check for combined formats in value attributes
        const elementValue = element.value || element.getAttribute('value') || '';
        const fullText = text + ' ' + elementValue;
        const matches = fullText.match(/\b(XXS|XS|S|M|L|XL|XXL)(?:\/(?:XXS|XS|S|M|L|XL|XXL))?\b/gi);
        if (matches) {
          sizeValues = sizeValues.concat(matches.map(m => m.trim().toUpperCase()));
        }
      });
    });
    
    return [...new Set(sizeValues)]; // Remove duplicates
  }
  
  // Check various places where sizes might appear
  const selectors = [
    // Variant picker options
    '.size-option',
    '[data-option-name*="size" i] input',
    '[data-option-name*="size" i] label',
    'input[type="radio"][name*="size"]',
    'input[type="radio"][value*="Size"]',
    'label[for*="size"]',
    // Product form options
    '.product-form__option',
    '.variant-option',
    '.product-form__input input',
    '.product-form__input label',
    // Select dropdowns and their options
    'select option',
    'select[name*="size" i] option',
    'select[name*="Size"] option',
    '.product-form select option',
    // Bra size picker
    '.bra-size-button',
    '.bra-size-option'
  ];
  
  const sizeValues = extractSizeValues(selectors);
  // Analyze the format
  const hasCombinedFormat = sizeValues.some(size => size.includes('/'));
  const hasSingleFormat = sizeValues.some(size => /^(XXS|XS|S|M|L|XL|XXL)$/.test(size));

  // Determine format based on what we found
  // Priority: Combined format is more specific, so prefer it when detected
  if (hasCombinedFormat) {
    return 'combined';
  } else if (hasSingleFormat) {
    return 'single';
  } else {
    // Default to combined format if no clear detection
    return 'combined';
  }
}

// Function to detect if Bordelle product uses alphabet (XS, S, M, L) or numeric (1, 2, 3, 4) format
function detectBordelleFormat() {
  // Function to extract size values from various elements
  function extractSizeValues(selectors) {
    let sizeValues = [];
    
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        const text = element.textContent || element.value || element.getAttribute('data-value') || '';
        // Look for size patterns like XS, S, M, L, XL and numeric 1, 2, 3, 4
        const elementValue = element.value || element.getAttribute('value') || '';
        const fullText = text + ' ' + elementValue;
        
        // Look for alphabet sizes (XS, S, M, L, XL)
        const alphabetMatches = fullText.match(/\b(XXS|XS|S|M|L|XL|XXL)\b/gi);
        if (alphabetMatches) {
          sizeValues = sizeValues.concat(alphabetMatches.map(m => m.trim().toUpperCase()));
        }
        
        // Look for numeric sizes (1, 2, 3, 4, 5) - be careful to avoid other numbers
        // Only match single digits that are likely sizes (not measurements, prices, etc.)
        const numericMatches = fullText.match(/\b[1-5]\b/g);
        if (numericMatches) {
          sizeValues = sizeValues.concat(numericMatches.map(m => m.trim()));
        }
      });
    });
    
    return [...new Set(sizeValues)]; // Remove duplicates
  }
  
  // Check various places where sizes might appear
  const selectors = [
    // Variant picker options
    '.size-option',
    '[data-option-name*="size" i] input',
    '[data-option-name*="size" i] label',
    'input[type="radio"][name*="size"]',
    'input[type="radio"][value*="Size"]',
    'label[for*="size"]',
    // Product form options
    '.product-form__option',
    '.variant-option',
    '.product-form__input input',
    '.product-form__input label',
    // Select dropdowns and their options
    'select option',
    'select[name*="size" i] option',
    'select[name*="Size"] option',
    '.product-form select option',
    // Bra size picker
    '.bra-size-button',
    '.bra-size-option'
  ];
  
  const sizeValues = extractSizeValues(selectors);
  // Analyze the format
  const hasAlphabetFormat = sizeValues.some(size => /^(XXS|XS|S|M|L|XL|XXL)$/.test(size));
  const hasNumericFormat = sizeValues.some(size => /^[1-5]$/.test(size));

  // Determine format based on what we found
  // Priority: If we find numeric, assume numeric format since it's more specific for Bordelle
  if (hasNumericFormat) {
    return 'numeric';
  } else if (hasAlphabetFormat) {
    return 'alphabet';
  } else {
    // Default to alphabet format if no clear detection
    return 'alphabet';
  }
}

// Function to detect if Agent Provocateur product uses alphabet (XS, S, M, L) or numeric (1, 2, 3, 4, 5) format
function detectAgentProvocateurFormat() {
  // Function to extract size values from various elements
  function extractSizeValues(selectors) {
    let sizeValues = [];
    
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        const text = element.textContent || element.value || element.getAttribute('data-value') || '';
        // Look for size patterns like XS, S, M, L, XL and numeric 1, 2, 3, 4, 5
        const elementValue = element.value || element.getAttribute('value') || '';
        const fullText = text + ' ' + elementValue;
        
        // Look for alphabet sizes (XS, S, M, L, XL)
        const alphabetMatches = fullText.match(/\b(XXS|XS|S|M|L|XL|XXL)\b/gi);
        if (alphabetMatches) {
          sizeValues = sizeValues.concat(alphabetMatches.map(m => m.trim().toUpperCase()));
        }
        
        // Look for numeric sizes (1, 2, 3, 4, 5) - be careful to avoid other numbers
        // Only match single digits that are likely sizes (not measurements, prices, etc.)
        const numericMatches = fullText.match(/\b[1-5]\b/g);
        if (numericMatches) {
          sizeValues = sizeValues.concat(numericMatches.map(m => m.trim()));
        }
      });
    });
    
    return [...new Set(sizeValues)]; // Remove duplicates
  }
  
  // Check various places where sizes might appear
  const selectors = [
    // Variant picker options
    '.size-option',
    '[data-option-name*="size" i] input',
    '[data-option-name*="size" i] label',
    'input[type="radio"][name*="size"]',
    'input[type="radio"][value*="Size"]',
    'label[for*="size"]',
    // Product form options
    '.product-form__option',
    '.variant-option',
    '.product-form__input input',
    '.product-form__input label',
    // Select dropdowns and their options
    'select option',
    'select[name*="size" i] option',
    'select[name*="Size"] option',
    '.product-form select option',
    // Bra size picker
    '.bra-size-button',
    '.bra-size-option'
  ];
  
  const sizeValues = extractSizeValues(selectors);
  // Analyze the format
  const hasAlphabetFormat = sizeValues.some(size => /^(XXS|XS|S|M|L|XL|XXL)$/.test(size));
  const hasNumericFormat = sizeValues.some(size => /^[1-5]$/.test(size));

  // Determine format based on what we found
  // Priority: If we find numeric, assume numeric format since it's more specific for Agent Provocateur
  if (hasNumericFormat) {
    return 'numeric';
  } else if (hasAlphabetFormat) {
    return 'alphabet';
  } else {
    // Default to alphabet format if no clear detection
    return 'alphabet';
  }
}

// Function to detect if Agent Provocateur underwear uses alphabet (XS, S, M, L) or numeric (1, 2, 3, 4, 5) format
function detectAgentProvocateurUnderwearFormat() {
  // Function to extract size values from various elements
  function extractSizeValues(selectors) {
    let sizeValues = [];
    
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        const text = element.textContent || element.value || element.getAttribute('data-value') || '';
        // Look for size patterns like XS, S, M, L, XL and numeric 1, 2, 3, 4, 5
        const elementValue = element.value || element.getAttribute('value') || '';
        const fullText = text + ' ' + elementValue;
        
        // Look for alphabet sizes (XS, S, M, L, XL)
        const alphabetMatches = fullText.match(/\b(XXS|XS|S|M|L|XL|XXL)\b/gi);
        if (alphabetMatches) {
          sizeValues = sizeValues.concat(alphabetMatches.map(m => m.trim().toUpperCase()));
        }
        
        // Look for numeric sizes (1, 2, 3, 4, 5) - be careful to avoid other numbers
        // Only match single digits that are likely sizes (not measurements, prices, etc.)
        const numericMatches = fullText.match(/\b[1-5]\b/g);
        if (numericMatches) {
          sizeValues = sizeValues.concat(numericMatches.map(m => m.trim()));
        }
      });
    });
    
    return [...new Set(sizeValues)]; // Remove duplicates
  }
  
  // Check various places where sizes might appear
  const selectors = [
    // Variant picker options
    '.size-option',
    '[data-option-name*="size" i] input',
    '[data-option-name*="size" i] label',
    'input[type="radio"][name*="size"]',
    'input[type="radio"][value*="Size"]',
    'label[for*="size"]',
    // Product form options
    '.product-form__option',
    '.variant-option',
    '.product-form__input input',
    '.product-form__input label',
    // Select dropdowns and their options
    'select option',
    'select[name*="size" i] option',
    'select[name*="Size"] option',
    '.product-form select option'
  ];
  
  const sizeValues = extractSizeValues(selectors);
  // Analyze the format
  const hasAlphabetFormat = sizeValues.some(size => /^(XXS|XS|S|M|L|XL|XXL)$/.test(size));
  const hasNumericFormat = sizeValues.some(size => /^[1-5]$/.test(size));

  // Determine format based on what we found
  // Priority: If we find numeric, assume numeric format since it's more specific for Agent Provocateur
  if (hasNumericFormat) {
    return 'numeric';
  } else if (hasAlphabetFormat) {
    return 'alphabet';
  } else {
    // Default to alphabet format if no clear detection
    return 'alphabet';
  }
}

// Function to detect if Bordelle underwear uses alphabet (XS, S, M, L) or numeric (1, 2, 3, 4, 5) format
function detectBordelleUnderwearFormat() {
  // Function to extract size values from various elements
  function extractSizeValues(selectors) {
    let sizeValues = [];
    
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        const text = element.textContent || element.value || element.getAttribute('data-value') || '';
        // Look for size patterns like XS, S, M, L, XL and numeric 1, 2, 3, 4, 5
        const elementValue = element.value || element.getAttribute('value') || '';
        const fullText = text + ' ' + elementValue;
        
        // Look for alphabet sizes (XS, S, M, L, XL)
        const alphabetMatches = fullText.match(/\b(XXS|XS|S|M|L|XL|XXL)\b/gi);
        if (alphabetMatches) {
          sizeValues = sizeValues.concat(alphabetMatches.map(m => m.trim().toUpperCase()));
        }
        
        // Look for numeric sizes (1, 2, 3, 4, 5) - be careful to avoid other numbers
        // Only match single digits that are likely sizes (not measurements, prices, etc.)
        const numericMatches = fullText.match(/\b[1-5]\b/g);
        if (numericMatches) {
          sizeValues = sizeValues.concat(numericMatches.map(m => m.trim()));
        }
      });
    });
    
    return [...new Set(sizeValues)]; // Remove duplicates
  }
  
  // Check various places where sizes might appear
  const selectors = [
    // Variant picker options
    '.size-option',
    '[data-option-name*="size" i] input',
    '[data-option-name*="size" i] label',
    'input[type="radio"][name*="size"]',
    'input[type="radio"][value*="Size"]',
    'label[for*="size"]',
    // Product form options
    '.product-form__option',
    '.variant-option',
    '.product-form__input input',
    '.product-form__input label',
    // Select dropdowns and their options
    'select option',
    'select[name*="size" i] option',
    'select[name*="Size"] option',
    '.product-form select option'
  ];
  
  const sizeValues = extractSizeValues(selectors);
  // Analyze the format
  const hasAlphabetFormat = sizeValues.some(size => /^(XXS|XS|S|M|L|XL|XXL)$/.test(size));
  const hasNumericFormat = sizeValues.some(size => /^[1-5]$/.test(size));

  // Determine format based on what we found
  // Priority: If we find numeric, assume numeric format since it's more specific for Bordelle
  if (hasNumericFormat) {
    return 'numeric';
  } else if (hasAlphabetFormat) {
    return 'alphabet';
  } else {
    // Default to alphabet format if no clear detection
    return 'alphabet';
  }
}

// Function to detect current product size format
function detectProductSizeFormat() {
  
  // Function to extract and analyze size values from text
  function extractSizeValues(text) {
    if (!text) return [];
    
    // Extract all potential size values from the text
    // Enhanced pattern to capture combined wireless sizes like XS/S, M/L
    const sizeMatches = text.match(/\b(?:\d{2}[A-Z]{1,3}(?:\/[A-Z]{1,3})*|(?:XXS|XS|S|M|L|XL|XXL)(?:\/(?:XXS|XS|S|M|L|XL|XXL))?|\d)\b/gi);
    return sizeMatches ? sizeMatches.map(s => s.trim().toUpperCase()) : [];
  }
  
  // Function to classify size values
  function classifySizes(sizeValues) {
    const braPattern = /^\d{2}[A-Z]{1,3}(?:\/[A-Z]{1,3})*$/; // 32B, 34DD, 36E/DD, 30F/DDD/E
    const wirelessLetterPattern = /^(XXS|XS|S|M|L|XL|XXL)$/; // S, M, L, XL (single sizes)
    const wirelessCombinedPattern = /^(XXS|XS|S|M|L|XL|XXL)\/(XXS|XS|S|M|L|XL|XXL)$/; // XS/S, M/L (combined sizes)
    const wirelessNumberPattern = /^\d$/; // 0, 1, 2, 3
    
    let braCount = 0;
    let wirelessLetterCount = 0;
    let wirelessCombinedCount = 0;
    let wirelessNumberCount = 0;
    
    sizeValues.forEach(size => {
      if (braPattern.test(size)) {
        braCount++;
      } else if (wirelessCombinedPattern.test(size)) {
        wirelessCombinedCount++;
      } else if (wirelessLetterPattern.test(size)) {
        wirelessLetterCount++;
      } else if (wirelessNumberPattern.test(size)) {
        wirelessNumberCount++;
      }
    });
    
    // Priority: Bra sizes take precedence over wireless keywords
    if (braCount > 0) {
      return 'bra';
    } else if (wirelessCombinedCount > 0) {
      return 'wireless';
    } else if (wirelessLetterCount > 0) {
      return 'wireless';
    } else if (wirelessNumberCount > 0) {
      return 'wireless';
    }
    
    return null;
  }
  
  // Method 1: Check size options in variant selectors
  const sizeOptions = document.querySelectorAll('.size-option, [data-option-name*="size" i], [data-option-name*="Size"], .variant-option, .product-form__option, select option, select[name*="size" i] option, select[name*="Size"] option');
  
  if (sizeOptions.length > 0) {
    let allSizeText = '';
    sizeOptions.forEach((option, index) => {
      const text = option.textContent || option.value || option.getAttribute('data-value') || '';
      
      allSizeText += ' ' + text;
    });
    

    const sizeValues = extractSizeValues(allSizeText);
    

    const classification = classifySizes(sizeValues);
    if (classification) {
      
      return classification;
    }
  }
  
  // Method 1.5: Check select dropdowns specifically (important for Chantelle)
  const selectElements = document.querySelectorAll('select');
  
  if (selectElements.length > 0) {
    let selectText = '';
    selectElements.forEach((select, index) => {
      const name = select.name || select.getAttribute('name') || '';
      const id = select.id || select.getAttribute('id') || '';
      
      
      // Check if this is a size-related select (broader check)
      if (name.toLowerCase().includes('size') || name.toLowerCase().includes('Size') || 
          id.toLowerCase().includes('size') || id.toLowerCase().includes('Size') ||
          name === 'Size' || name === 'size') {
        const options = select.querySelectorAll('option');
        
        options.forEach((option, optIndex) => {
          const optionText = option.textContent || option.value || '';
          
          selectText += ' ' + optionText;
        });
      }
    });
    
    if (selectText.trim()) {
      
      const sizeValues = extractSizeValues(selectText);
      
      
      const classification = classifySizes(sizeValues);
      if (classification) {
        
        return classification;
      }
    }
  }
  
  // Method 1.6: Check for any elements containing size text (Chantelle specific)
  const allElements = document.querySelectorAll('*');
  let chantelleSizeText = '';
  allElements.forEach(el => {
    const text = el.textContent || '';
    // Look specifically for combined size patterns in any element
    if (/\b(XS\/S|S\/M|M\/L|L\/XL)\b/i.test(text)) {
      
      chantelleSizeText += ' ' + text;
    }
  });
  
  if (chantelleSizeText.trim()) {
    
    const sizeValues = extractSizeValues(chantelleSizeText);
    
    
    const classification = classifySizes(sizeValues);
    if (classification) {
      
      return classification;
    }
  }
  
  // Method 2: Check variant radio buttons and labels
  const variantInputs = document.querySelectorAll('input[type="radio"][name*="size"], input[type="radio"][value*="Size"], label[for*="size"]');
  
  if (variantInputs.length > 0) {
    let variantText = '';
    variantInputs.forEach((input, index) => {
      const text = input.textContent || input.value || input.getAttribute('data-value') || '';
      
      variantText += ' ' + text;
    });
    
    
    
    const sizeValues = extractSizeValues(variantText);
    
    
    const classification = classifySizes(sizeValues);
    if (classification) {
      
      return classification;
    }
  }
  
  // Method 3: Check current product data
  if (window.currentProduct && window.currentProduct.variants) {
    const allVariantSizes = [];
    window.currentProduct.variants.forEach(variant => {
      if (variant.options) {
        const sizeOption = variant.options.find(opt => 
          opt.name && opt.name.toLowerCase().includes('size')
        );
        if (sizeOption && sizeOption.value) {
          allVariantSizes.push(sizeOption.value.toString().toUpperCase());
        }
      }
    });
    
    
    
    const classification = classifySizes(allVariantSizes);
    if (classification) {
      
      return classification;
    }
  }
  
  // Method 4: Check product title/type for wireless indicators (LOWER priority)
  const productTitle = document.querySelector('.product-title, .product__title, h1')?.textContent || '';
  const productType = document.querySelector('.product-type, .product__type')?.textContent || '';
  
  const wirelessKeywords = ['wireless', 'bralette', 'soft cup', 'triangle', 'bandeau', 'sports bra', 'yoga'];
  const fullText = (productTitle + ' ' + productType).toLowerCase();
  
  if (wirelessKeywords.some(keyword => fullText.includes(keyword))) {
    
    return 'wireless';
  }
  
  // Default fallback - assume bra if uncertain
  
  return 'bra';
}

// International size converter - converts international size to current product's brand size
function convertInternationalSize() {
  const country = document.getElementById('countrySelect').value;
  const inputSize = document.getElementById('internationalSize').value.trim().toUpperCase();
  const result = document.getElementById('convertedResult');
  
  if (!inputSize) {
    result.textContent = 'Please enter a size';
    return;
  }
  
  
  
  // Step 1: Find the US size (matrix base reference) from the international size
  const usSize = findUSSize(inputSize, country);
  
  if (!usSize || !internationalSizeData[usSize]) {
    result.textContent = 'N/A';
    return;
  }
  
  
  
  // Step 2: Get current product brand and size format
  const currentBrand = getCurrentProductBrand();
  const productSizeFormat = detectProductSizeFormat();
  
  
  // Step 3: Display result based on product format
  let resultText = '';
  
  if (productSizeFormat === 'wireless') {
    // Product uses wireless sizing - get brand-specific wireless size or fallback to general
    resultText = sizeConversionData.getBrandWirelessSize(usSize, currentBrand);
  } else {
    // Product uses bra sizing - show bra size (Column E)
    resultText = `${usSize}`;
  }
  
  result.textContent = resultText;
  
  
}

// International underwear size converter - converts international underwear size to current product's brand size
function convertUnderwearInternationalSize() {
  const countrySelect = document.getElementById('underwearCountrySelect');
  const sizeInput = document.getElementById('underwearInternationalSize');
  const result = document.getElementById('underwearConvertedResult');
  
  if (!countrySelect || !sizeInput || !result) {
    console.error('Underwear converter elements not found');
    return;
  }
  
  const country = countrySelect.value;
  const inputSize = sizeInput.value.trim();
  
  if (!inputSize) {
    result.textContent = 'Please enter a size';
    return;
  }
  
  
  
  // Step 1: Find the US size (matrix base reference) from the international size
  const usSize = findUSUnderwearSize(inputSize, country);
  
  if (!usSize || !underwearInternationalSizeData[usSize]) {
    result.textContent = 'Size not found';
    
    return;
  }
  
  
  
  // Step 2: Get current product brand
  const currentBrand = getCurrentProductBrand();
  
  
  // Step 3: Convert to brand-specific underwear size if brand mapping exists
  let brandSize = null;
  if (currentBrand) {
    const brandKey = currentBrand.toLowerCase();
    
    // Use the underwear brand format detection to determine which mapping to use
    const detectedBrandKey = sizeConversionData.detectUnderwearBrandFormat(brandKey);
    
    
    const brandMapping = sizeConversionData.underwearBrandSizes[detectedBrandKey];
    
    
    if (brandMapping && brandMapping[usSize]) {
      brandSize = brandMapping[usSize];
      
    } 
  }
  
  // Step 4: Display result - use brand size if available, otherwise US base size
  let resultText = '';
  let displayNote = '';
  
  if (brandSize && brandSize !== usSize) {
    resultText = `${brandSize}`;
    displayNote = ` (${currentBrand} size)`;
  } else {
    resultText = `${usSize}`;
  }
  
  result.textContent = resultText + displayNote;
  
  
}

// Unit conversion functions
function inchesToCm(inches) {
  return inches * 2.54;
}

function cmToInches(cm) {
  return cm / 2.54;
}

// Toggle unit function
function toggleUnit(type) {
  const isBra = type === 'bra';
  const toggleBtn = document.getElementById(isBra ? 'braUnitToggle' : 'underwearUnitToggle');
  // Active (right position) = inches, Inactive (left position) = cm
  const currentUnit = toggleBtn.classList.contains('active') ? 'in' : 'cm';
  const newUnit = currentUnit === 'in' ? 'cm' : 'in';
  
  // Toggle button state
  toggleBtn.classList.toggle('active');
  
  if (isBra) {
    // Bra size inputs
    const bustInput = document.getElementById('bustInput');
    const underBustInput = document.getElementById('underBustInput');
    const bustUnit = bustInput.parentElement.querySelector('.unit');
    const underBustUnit = underBustInput.parentElement.querySelector('.unit');
    
    // Convert values
    if (bustInput.value) {
      const currentValue = parseFloat(bustInput.value);
      const newValue = newUnit === 'cm' ? inchesToCm(currentValue) : cmToInches(currentValue);
      bustInput.value = Math.round(newValue); // Round to whole number
      bustInput.setAttribute('data-unit', newUnit);
      // Update min/max for cm
      if (newUnit === 'cm') {
        bustInput.min = 50;
        bustInput.max = 152;
        bustInput.step = 1;
      } else {
        bustInput.min = 20;
        bustInput.max = 60;
        bustInput.step = 1;
      }
    } else {
      bustInput.setAttribute('data-unit', newUnit);
      if (newUnit === 'cm') {
        bustInput.min = 50;
        bustInput.max = 152;
        bustInput.step = 1;
      } else {
        bustInput.min = 20;
        bustInput.max = 60;
        bustInput.step = 1;
      }
    }
    
    if (underBustInput.value) {
      const currentValue = parseFloat(underBustInput.value);
      const newValue = newUnit === 'cm' ? inchesToCm(currentValue) : cmToInches(currentValue);
      underBustInput.value = Math.round(newValue); // Round to whole number
      underBustInput.setAttribute('data-unit', newUnit);
      // Update min/max for cm
      if (newUnit === 'cm') {
        underBustInput.min = 50;
        underBustInput.max = 127;
        underBustInput.step = 1;
      } else {
        underBustInput.min = 20;
        underBustInput.max = 50;
        underBustInput.step = 1;
      }
    } else {
      underBustInput.setAttribute('data-unit', newUnit);
      if (newUnit === 'cm') {
        underBustInput.min = 50;
        underBustInput.max = 127;
        underBustInput.step = 1;
      } else {
        underBustInput.min = 20;
        underBustInput.max = 50;
        underBustInput.step = 1;
      }
    }
    
    // Update unit labels
    bustUnit.textContent = newUnit === 'cm' ? 'cm' : 'inches';
    underBustUnit.textContent = newUnit === 'cm' ? 'cm' : 'inches';
  } else {
    // Underwear size inputs
    const waistInput = document.getElementById('waistInput');
    const hipInput = document.getElementById('hipInput');
    const waistUnit = waistInput.parentElement.querySelector('.unit');
    const hipUnit = hipInput.parentElement.querySelector('.unit');
    
    // Convert values
    if (waistInput.value) {
      const currentValue = parseFloat(waistInput.value);
      const newValue = newUnit === 'cm' ? inchesToCm(currentValue) : cmToInches(currentValue);
      waistInput.value = Math.round(newValue); // Round to whole number
      waistInput.setAttribute('data-unit', newUnit);
      // Update min/max for cm
      if (newUnit === 'cm') {
        waistInput.min = 50;
        waistInput.max = 152;
        waistInput.step = 1;
      } else {
        waistInput.min = 20;
        waistInput.max = 60;
        waistInput.step = 1;
      }
    } else {
      waistInput.setAttribute('data-unit', newUnit);
      if (newUnit === 'cm') {
        waistInput.min = 50;
        waistInput.max = 152;
        waistInput.step = 1;
      } else {
        waistInput.min = 20;
        waistInput.max = 60;
        waistInput.step = 1;
      }
    }
    
    if (hipInput.value) {
      const currentValue = parseFloat(hipInput.value);
      const newValue = newUnit === 'cm' ? inchesToCm(currentValue) : cmToInches(currentValue);
      hipInput.value = Math.round(newValue); // Round to whole number
      hipInput.setAttribute('data-unit', newUnit);
      // Update min/max for cm
      if (newUnit === 'cm') {
        hipInput.min = 50;
        hipInput.max = 152;
        hipInput.step = 1;
      } else {
        hipInput.min = 20;
        hipInput.max = 60;
        hipInput.step = 1;
      }
    } else {
      hipInput.setAttribute('data-unit', newUnit);
      if (newUnit === 'cm') {
        hipInput.min = 50;
        hipInput.max = 152;
        hipInput.step = 1;
      } else {
        hipInput.min = 20;
        hipInput.max = 60;
        hipInput.step = 1;
      }
    }
    
    // Update unit labels
    waistUnit.textContent = newUnit === 'cm' ? 'cm' : 'inches';
    hipUnit.textContent = newUnit === 'cm' ? 'cm' : 'inches';
  }
}

// Close modal when clicking outside
window.onclick = function(event) {
  const modal = document.getElementById('sizeFinder');
  if (event.target === modal) {
    closeSizeFinder();
  }
}

// Function to safely get stored data with fallbacks
function getStoredSizeFinderResults() {
  return StorageUtils.getItem('sizeFinderResults');
}

// Function to safely get stored underwear data
function getStoredUnderwearSizeFinderResults() {
  return StorageUtils.getItem('underwearSizeFinderResults');
}

// Function to get recommended size for current product
function getRecommendedSizeForProduct() {
  // Check product type first
  const productType = getCurrentProductType();
  
  if (productType && productType.toLowerCase() === 'underwear') {
    // For underwear products, get underwear size finder results
    const underwearResults = getStoredUnderwearSizeFinderResults();
    if (!underwearResults) {
      return null;
    }
    
    try {
      const results = JSON.parse(underwearResults);
      const baseUnderwearSize = results.baseUnderwearSize;
      
      
      
      // Get current product brand
      const currentBrand = getCurrentProductBrand();
      
      
      // For recommended-size-display: use brand-specific size if available
      // For recommendedUnderwearSize: always use alphabet format
      if (currentBrand) {
        const brandKey = currentBrand.toLowerCase();
        
        // Use the underwear brand format detection to determine which mapping to use
        const detectedBrandKey = sizeConversionData.detectUnderwearBrandFormat(brandKey);
        
        
        const brandMapping = sizeConversionData.underwearBrandSizes[detectedBrandKey];
        
        
        if (brandMapping && brandMapping[baseUnderwearSize]) {
          
          return brandMapping[baseUnderwearSize];
        } 
      }
      
      // Fallback to base underwear size
      return baseUnderwearSize;
    } catch (error) {
      console.error('Error parsing stored underwear size finder results:', error);
      return null;
    }
  } else {
    // For bra products, use existing logic
    const storedResults = getStoredSizeFinderResults();
    if (!storedResults) {
      return null;
    }
    
    try {
      const results = JSON.parse(storedResults);
      const baseBraSize = results.baseBraSize; // US size from finder
      
      
      
      // Detect current product size format
      const productSizeFormat = detectProductSizeFormat();
      
      
      // Get current product brand
      const currentBrand = getCurrentProductBrand();
      
      
      if (productSizeFormat === 'wireless') {
        // Product uses wireless sizing - get brand-specific wireless size or fallback to general
        
        const result = sizeConversionData.getBrandWirelessSize(baseBraSize, currentBrand);
        
        return result;
      } else {
        // Product uses bra sizing - return the base bra size (Column E)
        
        return baseBraSize;
      }
    } catch (error) {
      console.error('Error parsing stored size finder results:', error);
      return null;
    }
  }
}

// Function to get current product type
function getCurrentProductType() {
  // Method 1: Check window.currentProduct (set by Liquid)
  if (window.currentProduct && window.currentProduct.product_type) {
    return window.currentProduct.product_type;
  }

  
  
  // Method 2: Check global product object
  if (typeof product !== 'undefined' && product && product.product_type) {
    return product.product_type;
  }
  
  // Method 3: Check window.product
  if (window.product && window.product.product_type) {
    return window.product.product_type;
  }
  
  // Method 4: Try to get from DOM data attributes
  const productTypeElement = document.querySelector('[data-product-type]');
  if (productTypeElement) {
    return productTypeElement.getAttribute('data-product-type');
  }
  
  // Method 5: Try to get from meta tags or other DOM sources
  const metaProductType = document.querySelector('meta[name="product-type"]');
  if (metaProductType) {
    return metaProductType.getAttribute('content');
  }
  
  return null;
}

// Function to check if current product is a bra product
function isBraProduct() {
  const productType = getCurrentProductType();
  
  if (!productType) {
    
    return true; // Default to bra if we can't determine type
  }
  
  const normalizedType = productType.toLowerCase();
  
  
  // Check product type directly
  if (normalizedType === 'bras') {
    
    return true;
  }
  
  // Lingerie Sets contain bras, so treat them as bra products
  if (normalizedType === 'lingerie sets' || normalizedType === 'lingerie set') {
    
    return true;
  }
  
  if (normalizedType === 'underwear') {
    
    return false; // For now, will implement panty size conversion later
  }
  
  if (normalizedType === 'loungewear') {
    
    return false; // Don't show fit finder for loungewear
  }
  
  // For other types, use size format as backup detection
  const productSizeFormat = detectProductSizeFormat();
  // Wireless bras are still bras, so treat them as bra products
  if (productSizeFormat === 'bra' || productSizeFormat === 'wireless') {
    
    return true;
  }
  
  // Default: assume non-bra for unknown types
  
  return false;
}

// Function to check and hide quick finder for loungewear
function handleQuickFinderVisibility() {
  const productType = getCurrentProductType();
  
  
  if (!productType) {
    return; // Don't hide if we can't determine product type
  }
  
  const normalizedType = productType.toLowerCase();
  
  if (normalizedType === 'loungewear') {
    // Hide the entire quick finder block for loungewear
    const quickFinderBlocks = document.querySelectorAll('.product-block__quick_finder, .quick-finder-block, [data-quick-finder]');
    quickFinderBlocks.forEach(block => {
      block.style.display = 'none';
      
    });
    return true; // Indicate that quick finder was hidden
  }
  
  return false; // Quick finder should be visible
}

// Function to restore previous fit finder data
function restoreFitFinderData() {
  
  
  // Restore bra fit finder data
  const braResults = getStoredSizeFinderResults();
  if (braResults) {
    try {
      const results = JSON.parse(braResults);
      
      
      // Fill in the measurements
      const bustInput = document.getElementById('bustInput');
      const underBustInput = document.getElementById('underBustInput');
      
      if (bustInput && results.bust) {
        bustInput.value = results.bust;
      }
      if (underBustInput && results.underbust) {
        underBustInput.value = results.underbust;
      }
      
      // If we have all the data, also restore the results
      if (results.baseBraSize && results.bust && results.underbust) {
        // Get current product brand if on product page
        let wirelessSize;
        if (window.location.pathname.includes('/products/')) {
          const currentBrand = getCurrentProductBrand();
          wirelessSize = sizeConversionData.getBrandWirelessSize(results.baseBraSize, currentBrand);
        } else {
          // On quiz page or other pages, use general wireless size
          wirelessSize = sizeConversionData.getWirelessSize(results.baseBraSize);
        }
        
        const matchedSize = {
          bra: results.baseBraSize,
          wireless: wirelessSize
        };
        
        displayResults(matchedSize, results.bust, results.underbust);
      }
      
    } catch (error) {
      console.error('Error restoring bra fit finder data:', error);
    }
  }
  
  // Restore underwear fit finder data
  const underwearResults = getStoredUnderwearSizeFinderResults();
  if (underwearResults) {
    try {
      const results = JSON.parse(underwearResults);
      
      
      // Fill in the measurements
      const waistInput = document.getElementById('waistInput');
      const hipInput = document.getElementById('hipInput');
      
      if (waistInput && results.waist) {
        waistInput.value = results.waist;
      }
      if (hipInput && results.hip) {
        hipInput.value = results.hip;
      }
      
      // If we have all the data, also restore the results
      if (results.baseUnderwearSize && results.waist && results.hip) {
        displayUnderwearResults(results.baseUnderwearSize, results.waist, results.hip);
      }
      
    } catch (error) {
      console.error('Error restoring underwear fit finder data:', error);
    }
  }
}

// DEBUG: Function to trace the entire recommended size process
function debugRecommendedSizeProcess() {
 
  
  // Check stored results
  const storedResults = getStoredSizeFinderResults();
  
  
  if (storedResults) {
    try {
      const results = JSON.parse(storedResults);
     
    } catch (e) {
      console.error('Error parsing stored results:', e);
    }
  } else {
    console.warn('⚠️ NO STORED SIZE FINDER RESULTS FOUND!');
   
    return null;
  }
  
  // Test detection functions
  
  const format = detectProductSizeFormat();
  
  
  
  const brand = getCurrentProductBrand();
  
  
  
  const recommendedSize = getRecommendedSizeForProduct();
  
  
 
  const testSize = '34C';
  const testBrand = 'Chantelle';
  const testResult = sizeConversionData.getBrandWirelessSize(testSize, testBrand);

  return recommendedSize;
}

// Expose debug function to global scope for manual testing
window.debugRecommendedSize = debugRecommendedSizeProcess;

// Global debug function to test bra size availability
window.debugBraSizeAvailability = function() {
  
  
  const bandRadios = document.querySelectorAll('input[name="bra_band_size"]');
  const cupRadios = document.querySelectorAll('input[name="bra_cup_size"]');
  
 
  const allBands = Array.from(bandRadios).map(radio => radio.value);
  const allCups = Array.from(cupRadios).map(radio => radio.value);

  // Test availability for each band
  allBands.forEach(band => {
    const isAvailable = checkBandAvailability(band);
    
  });
  
  // Test availability for each cup
  allCups.forEach(cup => {
    const isAvailable = checkCupAvailability(cup);
    
  });
  
  // Test specific combinations
  allBands.forEach(band => {
    allCups.forEach(cup => {
      const combination = checkBandCupCombination(band, cup);
      if (combination) {
      }
    });
  });
  
  // Test the recommended size
  const recommendedSize = getRecommendedSizeForProduct();
  if (recommendedSize) {
    
    const match = recommendedSize.match(/^(\d{2,3})([A-Z]+(?:\/[A-Z]+)*)$/);
    if (match) {
      const recommendedBand = match[1];
      const recommendedCup = match[2];
      
      const bandAvailable = checkBandAvailability(recommendedBand);
      const cupAvailable = checkCupAvailability(recommendedCup);
      const combinationAvailable = checkBandCupCombination(recommendedBand, recommendedCup);
      
      autoSelectRecommendedVariant();
    }
  }
  
  
};

// Function to update recommended size display
function updateRecommendedSizeDisplay() {
  // First check if we should hide the entire quick finder for loungewear
  if (handleQuickFinderVisibility()) {
    return; // Exit early if quick finder is hidden
  }
  
  // Check if current product is a bra product
  // Check if this is a product that should show size recommendations
  const productType = getCurrentProductType();
  const isBra = isBraProduct();
  const isUnderwear = productType && productType.toLowerCase() === 'underwear';
  
  if (!isBra && !isUnderwear) {
    // Hide any existing displays for products that don't support size recommendations
    const existingDisplays = document.querySelectorAll('.recommended-size-display');
    existingDisplays.forEach(display => display.style.display = 'none');
    
    return;
  }
  
  let recommendedSize;
  
  if (isUnderwear) {
    // For underwear, get the brand-specific size for the recommended-size-display
    const underwearResults = getStoredUnderwearSizeFinderResults();
    if (!underwearResults) {
      // No stored results - hide any existing displays
      const existingDisplays = document.querySelectorAll('.recommended-size-display');
      existingDisplays.forEach(display => display.style.display = 'none');
      return;
    }
    
    try {
      const results = JSON.parse(underwearResults);
      const baseUnderwearSize = results.baseUnderwearSize;
      const brandSize = results.brandSize;
      
      // Use brand-specific size if available, otherwise use alphabet size
      recommendedSize = brandSize || baseUnderwearSize;
      
      
    } catch (error) {
      console.error('Error parsing stored underwear size finder results:', error);
      const existingDisplays = document.querySelectorAll('.recommended-size-display');
      existingDisplays.forEach(display => display.style.display = 'none');
      return;
    }
  } else {
    // For bra products, use the existing logic
    recommendedSize = getRecommendedSizeForProduct();
    
    if (!recommendedSize) {
      // No stored results or error - hide any existing displays
      const existingDisplays = document.querySelectorAll('.recommended-size-display');
      existingDisplays.forEach(display => display.style.display = 'none');
      return;
    }
  }
  
  // Find fit finder buttons to add the recommended size display
  const fitFinderButtons = document.querySelectorAll('[data-fit-finder], .size-finder-btn');
  
  fitFinderButtons.forEach(button => {
    // Check if display already exists
    let display = button.parentElement.querySelector('.recommended-size-display');
    
    if (!display) {
      // Create new display element
      display = document.createElement('div');
      display.className = 'recommended-size-display';
     
      
      // Insert after the button
      button.parentElement.insertBefore(display, button.nextSibling);
    }
    
    // Update display text based on product type
    const sizeType = isUnderwear ? 'underwear' : 'bra';
    
    display.innerHTML = `<span>Your recommended ${sizeType} size is: <strong>${recommendedSize}</strong></span>`;
    display.style.display = 'flex';
  });
  
}

// Initialize when page loads
// Initialize unit toggles on page load
function initializeUnitToggles() {
  // Set default state: inches (active = right position)
  const braToggle = document.getElementById('braUnitToggle');
  const underwearToggle = document.getElementById('underwearUnitToggle');
  
  if (braToggle && !braToggle.classList.contains('active')) {
    // Default to inches (active state)
    braToggle.classList.add('active');
  }
  
  if (underwearToggle && !underwearToggle.classList.contains('active')) {
    // Default to inches (active state)
    underwearToggle.classList.add('active');
  }
}

document.addEventListener('DOMContentLoaded', function() {
  initializeUnitToggles();
  // Add debug trace for Chantelle products
  if (window.location.href.includes('chantelle') || window.location.href.includes('softstretch')) {
    setTimeout(() => {
      debugRecommendedSizeProcess();
    }, 2000); // Wait 2 seconds for page to fully load
  }
  
  // Initialize tabs based on product type
  initializeTabs();
  
  // Update recommended size display on page load
  updateRecommendedSizeDisplay();
  
  // Auto-select recommended size after a short delay to ensure DOM is ready
  setTimeout(() => {
    autoSelectRecommendedVariant();
  }, 500);
  
  // Debug: Test Chantelle format detection on page load
  const currentBrand = getCurrentProductBrand();
  if (currentBrand && currentBrand.toLowerCase() === 'chantelle') {
    const format = detectChantelleFormat();
  }
  
  // Add event listeners for automatic international size conversion
  const internationalSizeInput = document.getElementById('internationalSize');
  const countrySelect = document.getElementById('countrySelect');
  
  if (internationalSizeInput) {
    // Convert on input with debounce to avoid excessive calls
    let conversionTimeout;
    
    internationalSizeInput.addEventListener('input', function() {
      clearTimeout(conversionTimeout);
      conversionTimeout = setTimeout(() => {
        if (this.value.trim()) {
          convertInternationalSize();
        } else {
          // Clear result if input is empty
          const result = document.getElementById('convertedResult');
          if (result) result.textContent = '';
        }
      }, 500); // 500ms delay after user stops typing
    });
    
    // Also convert on Enter key press
    internationalSizeInput.addEventListener('keypress', function(e) {
      if (e.key === 'Enter') {
        clearTimeout(conversionTimeout);
        if (this.value.trim()) {
          convertInternationalSize();
        }
      }
    });
  }
  
  if (countrySelect) {
    // Clear input and result when country changes
    countrySelect.addEventListener('change', function() {
      const sizeInput = document.getElementById('internationalSize');
      const result = document.getElementById('convertedResult');
      
      if (sizeInput) {
        sizeInput.value = ''; // Clear the input field
      }
      if (result) {
        result.textContent = ''; // Clear the result
      }
    });
  }
  
  // Add event listeners for underwear international converter
  const underwearInternationalSizeInput = document.getElementById('underwearInternationalSize');
  const underwearCountrySelect = document.getElementById('underwearCountrySelect');
  
  if (underwearInternationalSizeInput) {
    // Convert on input with debounce to avoid excessive calls
    let underwearConversionTimeout;
    
    underwearInternationalSizeInput.addEventListener('input', function() {
      clearTimeout(underwearConversionTimeout);
      underwearConversionTimeout = setTimeout(() => {
        if (this.value.trim()) {
          convertUnderwearInternationalSize();
        } else {
          // Clear result if input is empty
          const result = document.getElementById('underwearConvertedResult');
          if (result) result.textContent = '';
        }
      }, 500); // 500ms delay after user stops typing
    });
    
    // Also convert on Enter key press
    underwearInternationalSizeInput.addEventListener('keypress', function(e) {
      if (e.key === 'Enter') {
        clearTimeout(underwearConversionTimeout);
        if (this.value.trim()) {
          convertUnderwearInternationalSize();
        }
      }
    });
  }
  
  if (underwearCountrySelect) {
    // Clear input and result when country changes
    underwearCountrySelect.addEventListener('change', function() {
      const sizeInput = document.getElementById('underwearInternationalSize');
      const result = document.getElementById('underwearConvertedResult');
      
      if (sizeInput) {
        sizeInput.value = ''; // Clear the input field
      }
      if (result) {
        result.textContent = ''; // Clear the result
      }
    });
  }
});

// Function to find the closest available size when recommended size is out of stock
function findClosestAvailableSize(recommendedSize, availableSizes) {
  if (!recommendedSize || !availableSizes || availableSizes.length === 0) {
    return null;
  }

  // Check if this is a bra size (contains both band and cup)
  const isBraSize = /^\d{2}[A-H]+$/.test(recommendedSize);
  
  if (isBraSize) {
    return findClosestBraSize(recommendedSize, availableSizes);
  } else {
    return findClosestGeneralSize(recommendedSize, availableSizes);
  }
}

// Function to find closest bra size
function findClosestBraSize(recommendedSize, availableSizes) {
  const parseBraSize = (size) => {
    const match = size.match(/^(\d{2})([A-H]+)$/);
    if (!match) return null;
    return {
      band: parseInt(match[1]),
      cup: match[2]
    };
  };

  const getCupOrder = (cupSize) => {
    const cupOrder = ['A', 'B', 'C', 'D', 'DD', 'E', 'F', 'G', 'H'];
    return cupOrder.indexOf(cupSize);
  };

  const recommended = parseBraSize(recommendedSize);
  if (!recommended) return availableSizes[0];

  let closestSize = null;
  let minDistance = Infinity;

  availableSizes.forEach(size => {
    const parsed = parseBraSize(size);
    if (!parsed) return;

    // Calculate distance: prioritize band size, then cup size
    const bandDiff = Math.abs(parsed.band - recommended.band);
    const cupDiff = Math.abs(getCupOrder(parsed.cup) - getCupOrder(recommended.cup));
    
    // Weight band difference more heavily than cup difference
    const distance = (bandDiff * 2) + cupDiff;

    if (distance < minDistance) {
      minDistance = distance;
      closestSize = size;
    }
  });

  return closestSize || availableSizes[0];
}

// Function to find closest general size (XS, S, M, L, XL, etc.)
function findClosestGeneralSize(recommendedSize, availableSizes) {
  const sizeOrder = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  
  const getIndex = (size) => {
    const index = sizeOrder.indexOf(size.toUpperCase());
    return index === -1 ? -1 : index;
  };

  const recommendedIndex = getIndex(recommendedSize);
  if (recommendedIndex === -1) return availableSizes[0];

  let closestSize = null;
  let minDistance = Infinity;

  availableSizes.forEach(size => {
    const sizeIndex = getIndex(size);
    if (sizeIndex === -1) return;

    const distance = Math.abs(sizeIndex - recommendedIndex);
    if (distance < minDistance) {
      minDistance = distance;
      closestSize = size;
    }
  });

  return closestSize || availableSizes[0];
}

// Function to auto-select recommended variant
function autoSelectRecommendedVariant() {
  const recommendedSize = getRecommendedSizeForProduct();
  
  if (!recommendedSize) {
    return;
  }

  // Find variant selectors
  const variantSelects = document.querySelector('variant-selects');
  const variantRadios = document.querySelector('variant-radios');
  
  if (variantRadios) {
    autoSelectInRadios(variantRadios, recommendedSize);
  } else if (variantSelects) {
    autoSelectInDropdowns(variantSelects, recommendedSize);
  }
}

function setCheckedAndDispatchIfChanged(input) {
  if (!input) return false;
  const wasChecked = input.checked;
  input.checked = true;

  if (wasChecked) {
    return false;
  }

  input.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

// Function to auto-select in radio buttons
function autoSelectInRadios(variantRadios, recommendedSize) {
  // Check if this is a bra product with the special bra size picker
  const bandSizeRadios = document.querySelectorAll('input[name="bra_band_size"]');
  const cupSizeRadios = document.querySelectorAll('input[name="bra_cup_size"]');
  
  if (bandSizeRadios.length > 0 && cupSizeRadios.length > 0) {
    // Use special bra size auto-selection
    autoSelectBraSize(recommendedSize, bandSizeRadios, cupSizeRadios);
    return;
  }

  const fieldsets = variantRadios.querySelectorAll('fieldset[data-option-name]');
  let sizeFieldset = null;

  // Find the size-related fieldset
  fieldsets.forEach(fieldset => {
    const optionName = fieldset.dataset.optionName.toLowerCase();
    if (optionName.includes('size') || optionName.includes('cup') || optionName.includes('band')) {
      sizeFieldset = fieldset;
    }
  });

  if (!sizeFieldset) {
    return;
  }

  // Get all available size options
  const sizeInputs = sizeFieldset.querySelectorAll('input[type="radio"]');
  const availableSizes = Array.from(sizeInputs)
    .filter(input => !input.disabled && !input.classList.contains('disabled'))
    .map(input => input.value);

  // Check if recommended size is available
  let targetSize = recommendedSize;
  if (!availableSizes.includes(recommendedSize)) {
    targetSize = findClosestAvailableSize(recommendedSize, availableSizes);
  }

  if (!targetSize) {
    return;
  }

  // Select the target size
  const targetInput = sizeFieldset.querySelector(`input[value="${targetSize}"]`);
  if (targetInput && !targetInput.disabled && !targetInput.classList.contains('disabled')) {
    setCheckedAndDispatchIfChanged(targetInput);
  }
}

// Function to get sister sizes for a given bra size
function getSisterSizes(braSize) {
  const band = parseInt(braSize.substring(0, 2));
  const cup = braSize.substring(2);
  
  const sisterSizes = [];
  
  // Sister sizes: same cup volume, different band sizes
  // Smaller band + larger cup
  if (band > 30) {
    const smallerBand = band - 2;
    const largerCup = getNextCupSize(cup);
    if (largerCup) {
      sisterSizes.push(smallerBand.toString().padStart(2, '0') + largerCup);
    }
  }
  
  // Larger band + smaller cup
  if (band < 44) {
    const largerBand = band + 2;
    const smallerCup = getPreviousCupSize(cup);
    if (smallerCup) {
      sisterSizes.push(largerBand.toString().padStart(2, '0') + smallerCup);
    }
  }
  
  return sisterSizes;
}

// Helper function to get next cup size
function getNextCupSize(cup) {
  const cupOrder = ['A', 'B', 'C', 'D', 'E/DD', 'DD', 'E', 'F/DDD/E', 'DDD', 'F', 'G/DDDD/F', 'DDDD', 'G', 'H'];
  const currentIndex = cupOrder.indexOf(cup);
  return currentIndex !== -1 && currentIndex < cupOrder.length - 1 ? cupOrder[currentIndex + 1] : null;
}

// Helper function to get previous cup size
function getPreviousCupSize(cup) {
  const cupOrder = ['A', 'B', 'C', 'D', 'E/DD', 'DD', 'E', 'F/DDD/E', 'DDD', 'F', 'G/DDDD/F', 'DDDD', 'G', 'H'];
  const currentIndex = cupOrder.indexOf(cup);
  return currentIndex > 0 ? cupOrder[currentIndex - 1] : null;
}

// Function to auto-select bra size using the special band/cup picker
function autoSelectBraSize(recommendedSize, bandSizeRadios, cupSizeRadios) {
  
  
  // Parse the recommended size (e.g., "38B") - improved regex to handle various cup formats
  const match = recommendedSize.match(/^(\d{2,3})([A-Z]+(?:\/[A-Z]+)*)$/);
  if (!match) {
    
    return;
  }
  
  const recommendedBand = match[1];
  const recommendedCup = match[2];
  
  // Get all band and cup radios (including disabled ones for filtering logic)
  const allBandRadios = Array.from(bandSizeRadios);
  const allCupRadios = Array.from(cupSizeRadios);
  
  // Check actual variant availability before filtering by disabled state
  const actuallyAvailableBands = allBandRadios
    .map(radio => radio.value)
    .filter(band => checkBandAvailability(band));
    
  const actuallyAvailableCups = allCupRadios
    .map(radio => radio.value)
    .filter(cup => checkCupAvailability(cup));

  
  // Find the best band size from actually available options
  let targetBand = recommendedBand;
  if (!actuallyAvailableBands.includes(recommendedBand)) {
    
    
    if (actuallyAvailableBands.length > 0) {
      const bandNumbers = actuallyAvailableBands.map(band => parseInt(band)).filter(num => !isNaN(num));
      const recommendedBandNum = parseInt(recommendedBand);
      
      if (bandNumbers.length > 0) {
        let closestBandNum = bandNumbers.reduce((prev, curr) => 
          Math.abs(curr - recommendedBandNum) < Math.abs(prev - recommendedBandNum) ? curr : prev
        );
        
        targetBand = closestBandNum.toString();
        
      }
    }
  }
  
  // Find the best cup size from actually available options
  let targetCup = recommendedCup;
  if (!actuallyAvailableCups.includes(recommendedCup)) {
    
    
    if (actuallyAvailableCups.length > 0) {
      // Extended cup order to handle complex cup sizes
      const cupOrder = ['A', 'B', 'C', 'D', 'E/DD', 'DD', 'E', 'F/DDD/E', 'DDD', 'F', 'G/DDDD/F', 'DDDD', 'G', 'H'];
      const recommendedCupIndex = cupOrder.indexOf(recommendedCup);
      
      if (recommendedCupIndex !== -1) {
        let closestCup = null;
        let minDistance = Infinity;
        
        actuallyAvailableCups.forEach(cup => {
          const cupIndex = cupOrder.indexOf(cup);
          if (cupIndex !== -1) {
            const distance = Math.abs(cupIndex - recommendedCupIndex);
            if (distance < minDistance) {
              minDistance = distance;
              closestCup = cup;
            }
          }
        });
        
        if (closestCup) {
          targetCup = closestCup;
        }
      } else {
        // If not found in standard order, just use first available
        targetCup = actuallyAvailableCups[0];
      }
    }
  }
  
  
  // EXACT SIZE APPROACH: Check exact recommended size first
  
  
  // Check if the exact recommended size is available
  if (checkBandCupCombination(recommendedBand, recommendedCup)) {
    // Select the exact recommended size
    const exactBandRadio = allBandRadios.find(radio => radio.value === recommendedBand);
    const exactCupRadio = allCupRadios.find(radio => radio.value === recommendedCup);
    
    if (exactBandRadio && exactCupRadio) {
      // Temporarily enable both radios if needed
      if (exactBandRadio.disabled) {
        exactBandRadio.disabled = false;
        
      }
      
      if (exactCupRadio.disabled) {
        exactCupRadio.disabled = false;
        
      }
      
      // STEP 1: Select exact band first
      setCheckedAndDispatchIfChanged(exactBandRadio);
      
      
      // STEP 2: Select exact cup after band selection
      setTimeout(() => {
        setCheckedAndDispatchIfChanged(exactCupRadio);
        
      }, 150);
      
      return; // Exit early since we found exact size
    }
  } 
  
  // SISTER SIZE APPROACH: Check sister sizes if exact size not available
  
  // Get sister sizes for the recommended size
  const sisterSizes = getSisterSizes(recommendedBand + recommendedCup);
  
  
  // Check if any sister size is available
  let selectedSisterSize = null;
  for (const sisterSize of sisterSizes) {
    const sisterBand = sisterSize.substring(0, 2);
    const sisterCup = sisterSize.substring(2);
    
    if (checkBandCupCombination(sisterBand, sisterCup)) {
      selectedSisterSize = sisterSize;
      
      break;
    }
  }
  
  if (selectedSisterSize) {
    // Select the available sister size
    const sisterBand = selectedSisterSize.substring(0, 2);
    const sisterCup = selectedSisterSize.substring(2);
    
    const sisterBandRadio = allBandRadios.find(radio => radio.value === sisterBand);
    const sisterCupRadio = allCupRadios.find(radio => radio.value === sisterCup);
    
    if (sisterBandRadio && sisterCupRadio) {
      // Temporarily enable both radios if needed
      if (sisterBandRadio.disabled) {
        sisterBandRadio.disabled = false;
        
      }
      
      if (sisterCupRadio.disabled) {
        sisterCupRadio.disabled = false;
        
      }
      
      // STEP 1: Select sister band first
      setCheckedAndDispatchIfChanged(sisterBandRadio);
      
      
      // STEP 2: Select sister cup after band selection
      setTimeout(() => {
        setCheckedAndDispatchIfChanged(sisterCupRadio);
        
      }, 150);
      
      return; // Exit early since we found sister size
    }
  } 
  
  
  
  // STEP 1: Select the best available band
  let selectedBand = targetBand;
  const targetBandRadio = allBandRadios.find(radio => radio.value === targetBand);
  
  if (targetBandRadio && actuallyAvailableBands.includes(targetBand)) {
    // Target band is available
    if (targetBandRadio.disabled) {
      targetBandRadio.disabled = false;
      
    }
    selectedBand = targetBand;
    
  } else {
    // Find the closest available band
    if (actuallyAvailableBands.length > 0) {
      const recommendedBandNum = parseInt(recommendedBand);
      const bandNumbers = actuallyAvailableBands.map(band => parseInt(band)).filter(num => !isNaN(num));
      
      if (bandNumbers.length > 0) {
        const closestBandNum = bandNumbers.reduce((prev, curr) => 
          Math.abs(curr - recommendedBandNum) < Math.abs(prev - recommendedBandNum) ? curr : prev
        );
        
        selectedBand = closestBandNum.toString();
       
      }
    }
  }
  
  // STEP 2: Select the band
  const finalBandRadio = allBandRadios.find(radio => radio.value === selectedBand);
  if (finalBandRadio) {
    if (finalBandRadio.disabled) {
      finalBandRadio.disabled = false;
    }
    setCheckedAndDispatchIfChanged(finalBandRadio);
    
    // STEP 3: Wait for band selection to update cup options, then select the best cup
    setTimeout(() => {
      // Find the best cup for the selected band
      let selectedCup = targetCup;
      
      // Get available cups for the selected band
      const availableCupsForSelectedBand = actuallyAvailableCups.filter(cup => 
        checkBandCupCombination(selectedBand, cup)
      );
      
      if (availableCupsForSelectedBand.includes(targetCup)) {
        // Target cup is available for this band
        selectedCup = targetCup;
      } else if (availableCupsForSelectedBand.length > 0) {
        // Find the closest available cup
        const cupOrder = ['A', 'B', 'C', 'D', 'E/DD', 'DD', 'E', 'F/DDD/E', 'DDD', 'F', 'G/DDDD/F', 'DDDD', 'G', 'H'];
        const targetCupIndex = cupOrder.indexOf(targetCup);
        
        if (targetCupIndex !== -1) {
          const availableCupIndices = availableCupsForSelectedBand
            .map(cup => cupOrder.indexOf(cup))
            .filter(index => index !== -1);

          if (availableCupIndices.length > 0) {
            const closestCupIndex = availableCupIndices.reduce((prev, curr) => {
              const prevDistance = Math.abs(prev - targetCupIndex);
              const currDistance = Math.abs(curr - targetCupIndex);
              return currDistance < prevDistance ? curr : prev;
            });
            
            selectedCup = cupOrder[closestCupIndex];
          }
        }
      }
      
      // STEP 4: Select the cup
      const finalCupRadio = allCupRadios.find(radio => radio.value === selectedCup);
      if (finalCupRadio) {
        if (finalCupRadio.disabled) {
          finalCupRadio.disabled = false;
        }
        setCheckedAndDispatchIfChanged(finalCupRadio);
      }
    }, 200); // Delay to allow UI to update after band selection
  }
}

// Helper function to check if a band size is actually available in variants
function checkBandAvailability(bandSize) {
  try {
    const variantRadiosElement = document.querySelector('variant-radios');
    if (!variantRadiosElement) return false;
    
    let variantData = [];
    
    if (typeof variantRadiosElement.getVariantData === 'function') {
      variantData = variantRadiosElement.getVariantData();
    } else {
      const variantScript = variantRadiosElement.querySelector('script[type="application/json"]');
      if (variantScript) {
        variantData = JSON.parse(variantScript.textContent);
      }
    }
    
    if (!variantData || variantData.length === 0) return false;
    
    // Check if any variant has a size starting with this band and is available
    return variantData.some(variant => {
      if (!variant.available) return false;
      
      const sizeOptions = [variant.option1, variant.option2, variant.option3].filter(o => o);
      return sizeOptions.some(option => {
        if (typeof option === 'string' && option.match(/^\d{2,3}[A-Z]/)) {
          const bandPart = option.match(/^(\d{2,3})/);
          return bandPart && bandPart[1] === bandSize;
        }
        return false;
      });
    });
  } catch (error) {
    return false;
  }
}

// Helper function to check if a cup size is actually available in variants
function checkCupAvailability(cupSize) {
  try {
    const variantRadiosElement = document.querySelector('variant-radios');
    if (!variantRadiosElement) return false;
    
    let variantData = [];
    
    if (typeof variantRadiosElement.getVariantData === 'function') {
      variantData = variantRadiosElement.getVariantData();
    } else {
      const variantScript = variantRadiosElement.querySelector('script[type="application/json"]');
      if (variantScript) {
        variantData = JSON.parse(variantScript.textContent);
      }
    }
    
    if (!variantData || variantData.length === 0) return false;
    
    // Check if any variant has a size ending with this cup and is available
    return variantData.some(variant => {
      if (!variant.available) return false;
      
      const sizeOptions = [variant.option1, variant.option2, variant.option3].filter(o => o);
      return sizeOptions.some(option => {
        if (typeof option === 'string' && option.match(/^\d{2,3}[A-Z]/)) {
          const cupPart = option.replace(/^\d{2,3}/, '');
          return cupPart === cupSize;
        }
        return false;
      });
    });
  } catch (error) {
    return false;
  }
}

// Helper function to check if a specific band+cup combination is available
function checkBandCupCombination(bandValue, cupValue) {
  try {
    const variantRadiosElement = document.querySelector('variant-radios');
    if (!variantRadiosElement) return false;
    
    let variantData = [];
    
    if (typeof variantRadiosElement.getVariantData === 'function') {
      variantData = variantRadiosElement.getVariantData();
    } else {
      const variantScript = variantRadiosElement.querySelector('script[type="application/json"]');
      if (variantScript) {
        variantData = JSON.parse(variantScript.textContent);
      }
    }
    
    if (!variantData || variantData.length === 0) return false;
    
    // Check if any variant has exactly this band+cup combination and is available
    const targetSize = bandValue + cupValue;
    const isAvailable = variantData.some(variant => {
      if (!variant.available) return false;
      
      const sizeOptions = [variant.option1, variant.option2, variant.option3].filter(o => o);
      return sizeOptions.some(option => {
        return typeof option === 'string' && option === targetSize;
      });
    });
    
    return isAvailable;
  } catch (error) {
    return false;
  }
}

// Function to auto-select in dropdown selects
function autoSelectInDropdowns(variantSelects, recommendedSize) {
  const selects = variantSelects.querySelectorAll('select');
  let sizeSelect = null;

  // Find the size-related select
  selects.forEach(select => {
    const label = select.previousElementSibling;
    if (label && label.textContent.toLowerCase().includes('size')) {
      sizeSelect = select;
    }
  });

  if (!sizeSelect) {
    return;
  }

  // Get all available size options
  const sizeOptions = Array.from(sizeSelect.options)
    .filter(option => !option.disabled)
    .map(option => option.value);

  // Check if recommended size is available
  let targetSize = recommendedSize;
  if (!sizeOptions.includes(recommendedSize)) {
    targetSize = findClosestAvailableSize(recommendedSize, sizeOptions);
  }

  if (!targetSize) {
    return;
  }

  // Select the target size
  const targetOption = sizeSelect.querySelector(`option[value="${targetSize}"]`);
  if (targetOption && !targetOption.disabled) {
    sizeSelect.value = targetSize;
    
    // Trigger change event to update the product
    const changeEvent = new Event('change', { bubbles: true });
    sizeSelect.dispatchEvent(changeEvent);
  }
}